/**
 * Rol çalışma alanlarının metni (`web/src/Workflow.tsx`).
 *
 * Metin burada **veri** olarak duruyor, ekranın içinde dize olarak değil: bir kelimeyi
 * değiştirmek için React bileşenini açmak gerekmesin, ve ekranda hangi cümlelerin geçtiği tek
 * bir yerden okunabilsin. Desen `ortak.ts` ile aynı; oradaki gerekçe burada tekrarlanmıyor.
 *
 * Gruplama ekrandaki panellere göre: `satis`, `operasyon`, `finans`, `gozetim`, `entegrasyon`,
 * `saltOkunur`. Bir metin iki panelde birden geçiyorsa `genel` altında duruyor, çünkü aynı
 * cümlenin iki kopyası bir gün ikiye ayrılır.
 *
 * Değişken alan içeren metinler işlev; tip zorlaması eksik bir değişkeni derleme hatasına
 * çeviriyor. İşlevler biçimlendirmeyi kendileri yapmıyor: çağıran `ortak.ts` içindeki
 * `para` / `tarihSaat` / `tamSayi` ile biçimlenmiş **hazır dizeyi** veriyor, çünkü para birimi
 * ve tarih kaynağı burada değil veride.
 *
 * Terimler `docs/tr-sozluk.md` ile karara bağlı. Orada çevrilmemesine karar verilen üç terim
 * burada da İngilizcesiyle duruyor: `voucher`, ürün ve tedarikçi adları, ve uç sözleşmesinin
 * kod değerleri (`MANUAL_LOCAL`, `NOT_CONFIGURED`).
 */

export const WORKFLOW_METIN = {
  /** Birden çok panelde geçen metinler. */
  genel: {
    istekBasarisiz: 'İstek başarısız oldu.',
    gecisReddedildi: 'Bu işlem kaydın şu anki durumunda yapılamaz. Kaydı yenileyip tekrar deneyin.',
    gecisYapilamaz: (varlik: string, onceki: string, sonraki: string) => `${varlik} şu anda "${onceki}" durumunda; "${sonraki}" durumuna geçirilemez.`,
    gecisZatenYapilmis: (varlik: string, durum: string) => `${varlik} zaten "${durum}" durumunda, yeni bir işlem gerekmiyor.`,
    surumCakisti: (varlik: string) => `${varlik} başka bir oturumda değiştirilmiş. Kaydı yenileyip tekrar deneyin.`,
    surumCakistiGenel: 'Kayıt başka bir oturumda değiştirilmiş. Kaydı yenileyip tekrar deneyin.',
    tamSayiUyarisi: 'Bu alana yalnız tam sayı yazılıyor.',
    /**
     * Para girişi artık **normal para biçiminde**: 250,00 avro için ekrana `250,00` yazılıyor.
     *
     * Eskiden alt birim isteniyordu (`25000`) ve aynı rakam sipariş detayında `€250,00`
     * görünüyordu: iki ekran iki dil konuşuyordu ve tek bir dalgınlık yüz kat fiyat yazıyordu.
     * Veri modeli değişmedi, alt birimde saklamak doğru karar; değişen, kullanıcıdan alt birim
     * istemenin yanlış karar olduğu. Çeviriyi arayüz yapıyor (`paraAltBirime`) ve **tam sayı
     * aritmetiğiyle** yapıyor: kayan noktalı bir çarpma kuruş kaybettirebilirdi.
     */
    tutarBosUyarisi: 'Bir tutar yazın.',
    tutarBicimiUyarisi: 'Tutarı para biçiminde yazın: en çok iki ondalık basamak (örnek: 250,00).',
    tutarIpucu: 'Örnek: 250,00',
    zorunluAlan: 'zorunlu alan',
    siparisSecin: 'Sipariş seçin',
    siparisSecinUyari: 'Önce bir sipariş seçin.',
    incelenecekSiparis: 'İncelenecek sipariş',
    sistem: 'sistem',
    atanmamis: 'Atanmamış',
    evet: 'evet',
    hayir: 'hayır',
    /** Sipariş ve teklif listelerinde satırın tek satırlık özeti. */
    kodDurum: (kod: string, durum: string) => `${kod} · ${durum}`,
    /** Aynısı, müşteri adıyla: sipariş seçen açılır listeler kod tek başına yetmiyor. */
    kodDurumMusteri: (kod: string, durum: string, musteri: string) => `${kod} · ${durum} · ${musteri}`,
    kalemSayisi: (adet: string) => `${adet} kalem`,
    /** Türkçede yüzde işareti sayının önünde duruyor: %65. */
    yuzde: (deger: string) => `%${deger}`,
  },

  /**
   * Sipariş kalemi önceliği ve akış varlıklarının adları.
   *
   * `ortak.ts` içindeki `DURUM_ADI` bunları taşımıyor: öncelik bir durum değil, varlık adı ise
   * hiç durum değil. Kod değerleri değişmiyor, yalnız ekranda karşılıkları yazılıyor.
   */
  oncelik: {
    LOW: 'Düşük',
    NORMAL: 'Normal',
    HIGH: 'Yüksek',
    URGENT: 'Acil',
  } as Readonly<Record<string, string>>,

  varlik: {
    Lead: 'Aday',
    Opportunity: 'Fırsat',
    Quote: 'Teklif',
    QuoteVersion: 'Teklif sürümü',
    Order: 'Sipariş',
    FulfilmentTask: 'Operasyon görevi',
    SupplierBooking: 'Tedarikçi rezervasyonu',
    Voucher: 'Voucher',
    PaymentEvidence: 'Ödeme kanıtı',
    Refund: 'İade',
    Reconciliation: 'Mutabakat',
    /**
     * Denetim izinde gerçekten geçen ve karşılığı olmayan hedef türleri (ölçüldü 28.08.2026:
     * `GET /audit` on üç farklı `targetType` döndürüyor, tablo yedisini tanımıyordu).
     *
     * En görünürü `Customer`: Denetim ekranı onu ham hâliyle yazıyordu ve `onyuz-kalite`nin
     * "ekranda İngilizce metin" kuralı onu yakalıyordu. Karşılığı olmayan değer olduğu gibi
     * kalmaya devam ediyor (`WORKFLOW_METIN.varlik[x] ?? x`); bu satırlar o "olduğu gibi"
     * listesini kısaltıyor.
     */
    Customer: 'Müşteri',
    Session: 'Oturum',
    GezipuanLedger: 'GeziPuan defteri',
    SupplierCancellation: 'Tedarikçi iptali',
    DocumentAsset: 'Belge',
    Route: 'Uç',
    User: 'Kullanıcı',
  } as Readonly<Record<string, string>>,

  satis: {
    baslik: 'Satış akışı',
    fiyatlamaKipi: 'Fiyatlama kipi: MANUAL_LOCAL (tanımlı dış kur kaynağı yok)',

    /**
     * FR-27: teklif toplamı yukarı bir tam birime yuvarlanıyor ve fark artık **yazılıyor**.
     *
     * Ölçüldü 28.08.2026: kalem 19,99 girilip teklif 20,00 görünüyordu; uç farkı
     * (`roundingAdjustmentMinor: 1`) dönderiyordu, ekran onu hiç okumuyordu. Kural bilinçli bir
     * ürün kararı, sessiz olması değil — muhasebe bir tahminle çalışmaz.
     */
    yuvarlamaFarki: (tutar: string) => `yuvarlama +${tutar}`,

    musteriBasligi: 'Müşteri',
    turEtiketi: 'Müşteri türü',
    adEtiketi: 'Müşteri adı',
    soyadEtiketi: 'Müşteri soyadı',
    /** CRM-A18: bireysel müşteride zorunlu, `assertIdentity` (`customers.service.ts`) uyguluyor. */
    tcKimlikEtiketi: 'TC Kimlik No',
    tcKimlikIpucu: 'Zorunlu değil, sonra da tamamlanabilir. Girildiyse 11 hane, ilk hane sıfır olamaz. Fatura kesilirken (B2C) isteniyor.',
    kurumsalAdEtiketi: 'Şirket adı',
    vergiNoEtiketi: 'Vergi/ticaret sicil no',
    telefonEtiketi: 'Telefon',
    /** Fiziksel/fatura adresi (17.10.2026): faturada ve teklif/proforma belgesinde alıcı adresi olarak basılır. */
    adresEtiketi: 'Fiziksel adres',
    adresIpucu: 'Fatura ve belgelerde alıcı adresi olarak yazılır. Tek satır, en fazla 140 karakter: cadde, bina, şehir, emirlik/bölge, ülke. Boş bırakılabilir, sonra da tamamlanabilir.',
    epostaEtiketi: 'Müşteri e-postası',
    epostaOrnegi: 'ad@ornek.invalid',
    musteriOlustur: 'Müşteri oluştur',
    musteriOlusturuldu: (ad: string) => `Müşteri oluşturuldu: ${ad}`,
    musteriAramaEtiketi: 'Müşteri ara',
    musteriAramaIpucu: 'İsim veya e-posta yazın',
    /** Aramanın kendi sonuç listesi, 21.09.2026 incelemesi. Sonuç artık kayıtlı müşteri açılırına düşmüyor. */
    musteriAramaSonucYok: 'Eşleşen müşteri yok.',
    /**
     * Para birimi ve beklenen değer, 21.09.2026 incelemesi. Ikisi de `createLead` çağrısında
     * sabit kodlanmıştı (`EUR` ve 1.000,00); ekranda hiç görünmedikleri için yanlış oldukları
     * da görünmüyordu. Üretimde `Currency` tablosu boş olduğu için sabit `EUR` her denemede
     * "Unknown or inactive currency: EUR" veriyordu.
     */
    paraBirimiEtiketi: 'Para birimi',
    beklenenDegerEtiketi: 'Beklenen değer',
    beklenenDegerIpucu: 'Tahmini satış tutarı. Boş bırakılabilir.',
    firsatBasligiIpucu: 'Boş bırakırsanız müşteri adı ve tarihten üretilir.',
    /**
     * Ürün Sahibi, 21.09.2026: "bunun amacı ne, ne fırsatı, bu haberim yok." Alan bir kod
     * terimini ekrana olduğu gibi taşıyordu. Cümle, adımın **neden** var olduğunu söylüyor.
     */
    firsatSecinIpucu: 'Fırsat, bir müşterinin tek bir seyahat isteğidir. Teklif ona bağlanır, böylece aynı müşterinin iki ayrı talebi birbirine karışmaz. Aşağıdan yeni bir fırsat açabilirsiniz.',
    musteriAramaEsigi: (harf: number) => `Sonuçları görmek için en az ${harf} harf yazın.`,

    /**
     * Teklif asistanı: talep alma (vaka adım 2-3, FR-01 - FR-05).
     *
     * **Ölçülen kusur: uçlar vardı, ekran yoktu.** `POST /sales/requests`, alan düzeltme, onay,
     * fiyat kutusu ve kalem önerisi 27.08.2026'da açıldı; satışçının müşteri mesajını
     * yapıştıracağı bir kutu hiçbir ekranda yoktu, yani akışın 2. ve 3. maddesi yalnız API'den
     * yürüyordu.
     *
     * Metnin tamamı ayrıştırmanın bir **tahmin** olduğunu söylüyor. Ekran onay istiyor, güven
     * istemiyor: modelin yanlış doldurduğu bir alanı yalnız satışçı görebilir, ve göremediği
     * bir tahmin sessizce teklife geçerdi.
     */
    talep: {
      baslik: 'Müşteri talebi (serbest metin)',
      aciklama:
        'Müşterinin yazdığı mesajı olduğu gibi yapıştırın. Asistan alanları doldurmayı dener. ' +
        'Doldurduğu her alan bir tahmindir ve siz onaylamadan teklife geçilmez.',
      metinEtiketi: 'Müşteri mesajı',
      metinIpucu: 'En az 10 karakter. Metinde telefon ya da e-posta geçiyorsa müşteri kaydı da aranır.',
      ayristir: 'Talebi al ve ayrıştır',
      metinBosUyari: 'Önce müşterinin mesajını yapıştırın.',
      alindi: 'Talep alındı.',
      alindiAyristiriliyor: 'Talep alındı. Alanlar dolduruluyor; bu sürede müşteriyi seçebilirsiniz.',
      ayristiriliyorAciklama: 'Talep kaydedildi, metinden alanlar çıkarılıyor. Bitince bu ekran kendiliğinden yenilenir.',
      talepDurumu: (kod: string, durum: string) => `Talep ${kod} · ${durum}`,

      /**
       * Dört ayrıştırma hâli, dört ayrı cümle.
       *
       * İkisi arıza (`NOT_CONFIGURED`, `FAILED`), biri normal bir sonuç (`PARSED_EMPTY`:
       * müşteri gerçekten bir şey yazmamış), biri başarı. Ölçüldü 27.08.2026: dördü de
       * temsilcinin ekranında **aynı boş form** olarak görünüyordu ve satışçı telefonda
       * müşteriyle konuşurken "sistem çalışmadı" ile "müşteri yazmamış" arasındaki farkı
       * bilemiyordu. Sebebin kendi cümlesi uçtan geliyor (`parsingReasonText`), burada
       * tekrarlanmıyor; buradaki yalnız hâlin adı.
       */
      ayristirmaDurumu: {
        NOT_CONFIGURED: 'Otomatik ayrıştırma yapılmadı',
        PENDING: 'Alanlar dolduruluyor',
        FAILED: 'Ayrıştırma başarısız oldu',
        PARSED_EMPTY: 'Ayrıştırıldı, dolacak alan çıkmadı',
        PARSED: 'Ayrıştırıldı',
      } as Readonly<Record<string, string>>,
      /** Kim üretti ve kaç kez denendi. Uçtaki `parsingProvider` bir kod değeri, çevrilmiyor. */
      ayristirmaKunyesi: (saglayici: string, deneme: string) => `Alanları üreten: ${saglayici}. Model denemesi: ${deneme}.`,
      ayristirmaHataKodu: (kod: string) => `Hata kodu: ${kod}`,

      /**
       * Müşteriden alınması gereken bilgilerin kontrol listesi.
       *
       * Ürün Sahibi, 21.09.2026 CRM incelemesi: "burada müşteriden alınması gereken bilgiler
       * diye ben bir kez yazmıştım ve öyle ayarlamıştık, burası patlak." Listeyi o gün yeniden
       * saydı: adı soyadı, hangi tarihler, kaç kişi, tatil konsepti (balayı mı aile tatili mi
       * ultra lüks aile tatili mi), çocuklar kaç yaşında, bütçe yaklaşık, istenen bölgeler
       * (otel tavsiyesi için), istenen oteller.
       *
       * Ekranda ayrıştırılan alanlar zaten çiziliyordu, ama **eksik olanın ne olduğunu** söyleyen
       * bir yer yoktu: on dört alanın hangisinin boş kaldığını görmek için hepsini tek tek
       * okumak gerekiyordu. Bu liste o okumayı ortadan kaldırıyor ve satışçıya müşteriye
       * soracağı soruyu veriyor.
       */
      kontrolBasligi: 'Müşteriden alınması gereken bilgiler',
      kontrolAciklamasi: (eksik: number) =>
        eksik === 0
          ? 'Sekiz bilginin hepsi elde. Teklife geçebilirsiniz.'
          : `${eksik} bilgi eksik. Müşteriye sormadan teklif hazırlamayın.`,
      kontrolVar: 'Var',
      kontrolEksik: 'Eksik',
      /** Çocuk yaşı yalnız çocuk varsa isteniyor; çocuksuz bir talepte eksik sayılmaz. */
      kontrolGerekmiyor: 'Gerekmiyor',
      kontrolSatirlari: {
        ad: 'Adı soyadı',
        tarih: 'Hangi tarihler',
        kisi: 'Kaç kişi',
        cocukYasi: 'Çocuklar kaç yaşında',
        konsept: 'Tatil konsepti',
        butce: 'Bütçe, yaklaşık',
        bolge: 'İstenen bölge',
        otel: 'İstenen otel',
      } as Readonly<Record<string, string>>,

      alanlarBasligi: 'Ayrıştırılan alanlar',
      alanlarAciklamasi:
        'Boş kalan alanı elle doldurun, yanlış olanı düzeltin. Düzelttiğiniz alan elle girilmiş sayılır ve düşük güven işareti kalkar.',
      girisCikisEtiketi: 'Giriş ve çıkış tarihi',
      /** FR-04'ün görünür yüzü. Rozet kısa; gerekçesi alanın kendi ipucunda yazılı. */
      dusukGuven: 'Düşük güven',
      guvenDusuk: (yuzde: string) => `Modelin tahmini, güven ${yuzde}. Göndermeden önce doğrulayın.`,
      guvenTahmin: (yuzde: string) => `Modelin tahmini, güven ${yuzde}.`,
      guvenElle: 'Elle girildi.',
      guvenAyristirilmadi: 'Ayrıştırılmadı; elle doldurun.',
      /**
       * Alan adları. Anahtar uçtaki alan adının kendisi; karşılığı olmayan bir anahtar geldiğinde
       * `ortak.ts` içindeki kuralla anahtarın kendisi yazılıyor, uydurulmuş bir karşılık değil.
       */
      alanAdi: {
        destination: 'Destinasyon',
        hotelName: 'Otel adı',
        checkIn: 'Giriş tarihi',
        checkOut: 'Çıkış tarihi',
        nights: 'Gece sayısı',
        adultCount: 'Yetişkin sayısı',
        childCount: 'Çocuk sayısı',
        childAges: 'Çocuk yaşları',
        budgetMinor: 'Bütçe',
        budgetCurrencyCode: 'Bütçe para birimi',
        hotelClass: 'Otel sınıfı',
        visaType: 'Vize tipi',
        visaDurationDays: 'Vize süresi (gün)',
        preferredServices: 'İstenen hizmetler',
      } as Readonly<Record<string, string>>,
      listeIpucu: 'Virgülle ayırın',
      paraBirimiIpucu: 'Üç harf, örnek: EUR',
      /**
       * Otel adı kozmetik bir alan değil: fiyat sorgusunun eşiği onun üstünde
       * (`price-trigger-eligibility.ts`, 29.08.2026). Şehir adıyla açılan bir sorgu katalogdan
       * gelişigüzel bir otel seçmek olurdu, o yüzden otel adı yoksa katalog hiç aranmıyor ve
       * fiyat kutusu boş kalıyor. Satışçı bunu bilmeden alanı atlarsa kutunun neden boş
       * olduğunu da bilemez.
       */
      otelAdiIpucu: 'Fiyat sorgusu bu alandan açılıyor; boşsa fiyat kutusu dolmaz.',
      /** Ay gün sayısına çevrilmiyor: "1 ay" sabit bir gün sayısı değil, çevirmek uydurmak olurdu. */
      visaSuresiIpucu: 'Gün sayısı. Talepte "1 aylık" gibi geçiyorsa boş bırakın.',
      /**
       * Bu ekranın düzeltemediği alan.
       *
       * Uç `forbidNonWhitelisted` ile çalışıyor: `UpdateQuoteRequestDto` içinde olmayan bir
       * anahtarı göndermek 400 döner. Bu yüzden ekran yalnız uçta kabul edildiğini bildiği
       * alanları düzenlenebilir çiziyor; kalanı okunur gösteriyor ve **gizlemiyor**. Gizlemek,
       * ayrıştırıcının doldurduğu bir alanı satışçıdan saklamak olurdu.
       */
      duzenlenemez: 'Bu alan bu ekrandan henüz düzeltilemiyor; değeri olduğu gibi gösteriliyor.',
      alanlariKaydet: 'Düzeltmeleri kaydet',
      duzeltmeYokUyari: 'Düzeltilen alan yok.',
      alanlarKaydedildi: (adet: string) => `${adet} alan düzeltildi.`,
      /** Boş bırakmak bir düzeltme değil: uç verilmeyen alanı dokunulmamış sayıyor, silmiyor. */
      bosDuzeltmeUyari: 'Boş bırakılan alan silinmiyor; uç yalnız dolu bir değeri yazabiliyor.',

      fiyatBasligi: 'Fiyat kutusu',
      fiyatAciklamasi:
        'Otel adı, tarih ve yolcu sayısı dolduğunda fiyat sorgusu arka planda başlar. Bu kutu o sorgunun bu anki hâlini gösterir; tedarikçiye buradan gidilmez.',
      fiyatiGetir: 'Fiyat kutusunu yenile',
      paximumBasligi: 'Paximum',
      roompilotBasligi: 'RoomPilot',
      fiyatBekliyor: 'Sorgu sürüyor; sonuç henüz yazılmadı.',
      fiyatKaynagi: (slug: string, an: string) => `${slug} · ${an}`,
      fiyatUrunu: (slug: string) => `Sorulan ürün: ${slug}`,

      onayBasligi: 'Onay ve müşteri',
      onayAciklamasi:
        'Onay bir müşteri kaydı ister. Onaylanmadan fırsat açılmaz ve teklif üretilmez.',
      eslesenMusteri: (ad: string, kanal: string) => `Eşleşen müşteri: ${ad} (${kanal} ile eşleşti)`,
      eslesmeKanali: { email: 'e-posta', phone: 'telefon' } as Readonly<Record<string, string>>,
      eslesmeSec: 'Bu müşteriyi seç',
      eslesmeYok: 'Metinde eşleşen müşteri kaydı bulunamadı. Listeden seçin ya da yukarıdan yeni müşteri oluşturun.',
      musteriGerekliUyari: 'Önce bir müşteri seçin.',
      onayla: 'Talebi onayla',
      onaylandi: 'Talep onaylandı; fırsat açılabilir.',

      firsatOlustur: 'Fırsat aç ve teklife geç',
      firsatAcildi: (kod: string) => `Fırsat açıldı: ${kod}. Teklif bölümünde seçili.`,

      oneriBasligi: 'Kalem önerileri',
      oneriAciklamasi:
        'Talepteki otel ya da destinasyon adıyla ürün ağacında aranır. Eklenen kalem, katalogdan eklenmiş kalemle aynı yoldan gider.',
      onerileriGetir: 'Kalem önerisi getir',
      oneriAramaYokUyari: 'Önce otel adı ya da destinasyon alanını doldurun.',
      oneriYok: 'Bu talebe uyan yayında ürün bulunamadı.',
      oneriSutun: {
        ad: 'Ürün',
        tur: 'Tür',
        fiyat: 'Referans satış fiyatı',
        eylem: 'Eylem',
      },
      /** FR-25: maliyet yalnız uç gönderdiğinde çiziliyor; satışçıya hiç gelmiyor. */
      oneriMaliyet: (tutar: string) => `Tedarikçi maliyeti: ${tutar}`,
      oneriMaliyetBayat: 'Bu maliyet gösterge niteliğinde; farklı bir tarih ya da oda dağılımına ait olabilir.',
      oneriMusaitlikNotu: 'Müsaitlik doğrulanmadı: bu liste canlı müsaitlik sinyali taşımıyor.',
    },

    adayBasligi: 'Aday ve fırsat',
    musteriSecin: 'Müşteri seçin',
    firsatBasligiEtiketi: 'Fırsat başlığı',
    olasilikEtiketi: 'Kazanma olasılığı yüzdesi',
    sonrakiEylemEtiketi: 'Sonraki eylem',
    crmNotlariEtiketi: 'CRM notları',
    adayKaynagiEtiketi: 'Aday kaynağı',
    adayOlustur: 'Aday oluştur',
    adayOlusturuldu: (kod: string) => `Aday oluşturuldu: ${kod}`,
    adaySecin: 'Aday seçin',
    adaySecinUyari: 'Önce bir aday seçin.',
    adayiDonustur: 'Adayı fırsata dönüştür',
    adayDonusturuldu: (kod: string) => `Aday fırsata dönüştürüldü: ${kod}`,
    firsatOlustur: 'Fırsat oluştur',
    firsatOlusturuldu: (kod: string, asama: string) => `Fırsat oluşturuldu: ${kod} (${asama})`,

    boruHattindaAra: 'Boru hattında ara',
    tumAsamalar: 'Tüm aşamalar',
    asamaFiltresiEtiketi: 'Fırsat aşaması filtresi',
    /** Fırsat satırı: kod, başlık, aşama, kazanma olasılığı, sahip, sonraki eylem. */
    firsatSatiri: (kod: string, baslik: string, asama: string, olasilik: string, sahip: string, sonraki: string) =>
      `${kod} · ${baslik} · ${asama} · ${olasilik} · sahip ${sahip} · sonraki eylem ${sonraki}`,
    asamayiIlerlet: 'Aşamayı ilerlet',
    /** Uca gerekçe olarak gidiyor ve denetim izinde okunuyor; o yüzden Türkçe. */
    asamaIlerlemeGerekcesi: 'boru hattı ilerlemesi',

    teklifBasligi: 'Teklif',
    firsatSecin: 'Fırsat seçin',
    /**
     * Kalem alanlarının etiketi **kısa**, sıra numarası kalemin kendi başlığında.
     *
     * Eskiden her etiket "1. kalemin birim fiyatı (alt birim)" diyordu ve hiçbiri ekranda
     * çizilmiyordu; yalnız `aria-label` içindeydiler. Ekrana çıkınca sıra numarasının her
     * satırda tekrarlanması gürültü oldu: numara bir kez, kalemin başlığında duruyor.
     */
    kalemBasligi: (sira: number) => `${sira}. kalem`,
    kalemTuruEtiketi: 'Tür',
    kalemAciklamasiEtiketi: 'Açıklama',
    kalemTedarikcisiEtiketi: 'Tedarikçi',
    kalemAdediEtiketi: 'Adet',
    kalemBirimFiyatiEtiketi: 'Birim fiyat',
    yolcuEtiketi: 'Yolcu',
    hizmetTarihiEtiketi: 'Hizmet tarihi',
    kalemVergisiEtiketi: 'Vergi',
    kalemUcretiEtiketi: 'Ücret',
    kalemIndirimiEtiketi: 'İndirim',
    kalemiSil: 'Kalemi sil',
    /**
     * Kalemin katalogdan geldiği **ekranda görünür**, ve kaldırılabilir.
     *
     * Bağın kendisi kadar önemli olan şey, yalan söylememesi: katalogdan A ürünü eklenip sonra
     * satır bambaşka bir şeye çevrilirse, slug artık yanlış bir ürünü gösterir. Yanlış bir bağ,
     * hiç bağ olmamasından kötüdür (aynı gerekçe `productSlug` yorumunda da yazılı). Çare, bağı
     * gizlemek değil göstermek ve tek tıkla koparılabilir yapmak.
     */
    kalemKatalogRozeti: (slug: string) => `Katalog: ${slug}`,
    kalemKatalogKaldir: 'Katalog bağını kaldır',
    kalemKatalogKaldirildi: 'Katalog bağı kaldırıldı; kalem elle yazılmış sayılıyor.',
    kalemEkle: 'Teklif kalemi ekle',
    /**
     * Katalogdan kalem ekleme.
     *
     * Ölçülen kusur: bir teklif kalemi sıfırdan yazılıyordu — tür, açıklama, tedarikçi, adet ve
     * **fiyat**, beşi de elle. Fiyatın elle yazılması yalnız tık harcamıyor, hata üretiyor:
     * satışçı ya ezberden yazıyor ya da ekranda hazır duran örnek veriyi üstüne yazmadan
     * gönderiyordu.
     *
     * Katalog **kopyalanmıyor**. Liste v3'ün sitede gösterdiği ürün ağacının ta kendisi
     * (`GET /catalogue/products`, `commerce:catalogue_read`). Yayında olmayan ürün listede yok
     * ve bu bir süzgeç değil, yayın kuralının kendisi: uç zaten yayımlanmamış ürünü göndermiyor.
     * CRM'in ikinci bir katalog tutması, iki fiyatın ayrışması demekti.
     */
    katalog: {
      baslik: 'Katalogdan kalem ekle',
      aciklama:
        'Liste, web sitesinin gösterdiği ürün ağacının aynısı. Yayında olmayan ürün burada da görünmüyor.',
      araEtiketi: 'Üründe ara',
      araIpucu: 'Ürün adı ya da türü',
      turSuzgeci: 'Ürün türü',
      tumTurler: 'Tüm türler',
      yukleniyor: 'Ürün ağacı okunuyor…',
      okunamadi: 'Ürün listesinin tamamı alınamadı. Yüklenen ürünleri kullanabilir veya yeniden deneyebilirsiniz.',
      /**
       * "Tanımlı değil" ile "okunamadı" ayrı iki cümle, çünkü ayrı iki durum ve çareleri ayrı:
       * biri bir yapılandırma eksiği (yönetici çözer), öteki geçici bir bağlantı sorunu
       * (yeniden denemek çözebilir). Tek bir cümle ikisini de "bir şeyler ters gitti"ye
       * çevirirdi. Ekranda ortam değişkeni adı geçmiyor; uç onları yazıyor, ekran yazmıyor.
       */
      tanimliDegil: 'Web sitesi bağlantısı tanımlı değil (NOT_CONFIGURED); ürün ağacı okunamıyor. Kalem elle yazılabilir.',
      bosSonuc: 'Aramanıza uyan yayında ürün yok.',
      sutun: {
        ad: 'Ürün',
        tur: 'Tür',
        fiyat: 'Başlangıç fiyatı',
        yayim: 'Yayımlandı',
        eylem: 'Eylem',
      },
      ekle: 'Kaleme ekle',
      /** Kalem eklendiğinde ne yapıldığı ve **ne yapılmadığı** birlikte söyleniyor. */
      eklendi: (ad: string) => `${ad} teklife eklendi. Fiyat katalogdan geldi; tedarikçiyi listeden seçin.`,
      eklendiTedarikciliyle: (ad: string, tedarikci: string) =>
        `${ad} teklife eklendi. Fiyat katalogdan, tedarikçi ${tedarikci}.`,
      fiyatYok: 'Bu ürünün katalogda başlangıç fiyatı yok; tutarı elle yazın.',
      sayac: (adet: string) => `${adet} yayında ürün`,
    },

    /**
     * Fiyat önerisi: satış akışı vakası adım 4-5. Yalnız katalogdan gelen (`productSlug` dolu)
     * kalemlerde görünüyor, çünkü uç bir v3 slug'ı istiyor; elle yazılmış bir kalemin karşılığı
     * yok.
     *
     * **FR-25, 27.08.2026 kararıyla daraldı**: uç `finance:read` yoksa kaynağı ve marj kuralını
     * hâlâ göndermiyor, ama tedarikçi maliyetini (`amount`/`currency`) artık gönderiyor
     * (`toSalesView`). Tutar `Workflow.tsx`teki `tedarikciMaliyetiYaz`in kendi `para()` çağrısıyla
     * biçimleniyor; burada ayrı bir "biliniyor" metnine gerek kalmadı.
     */
    fiyatOnerisi: {
      getir: 'Fiyat önerisi getir',
      tekrarGetir: 'Fiyat önerisini yenile (aynı sorgu, tedarikçiye gitmez)',
      tedarikciMaliyetiBasligi: 'Tedarikçi maliyeti',
      piyasaReferansiBasligi: 'Piyasa referansı (Booking/RoomPilot)',
      onerilenFiyatBasligi: 'Önerilen satış fiyatı',
      /**
       * Bayat uyarısı hem tedarikçi maliyeti hem öneri satırında ayrı ayrı çıkabilir; ikisi de
       * uçtaki `reasonText` cümlesini olduğu gibi gösteriyor, burada ikinci bir metin yazılmadı.
       */
      bayatBasligi: 'Bu fiyat bayat',
      /**
       * Piyasa fiyatı sorgusu sürerken. Bir hata cümlesi değil ve öyle okunmamalı: soğuk bir
       * Booking sorgusu yaklaşık yarım dakika sürüyor.
       */
      piyasaBekliyor: 'Sorgu sürüyor, yaklaşık yarım dakika',
      /**
       * Önerinin hangi yoldan geldiği. Satışçı sayıyı savunacaksa nereden çıktığını bilmeli;
       * `MARKET_SPREAD_BELOW_COST` özellikle gizlenmiyor, Ürün Sahibi'nin kararı bu
       * (29.08.2026): avantajlı kalamadığımız durum da satışçının göreceği bir bilgi.
       */
      oneriYolu: {
        MARKET_SPREAD: 'Piyasa fiyatına göre hesaplandı',
        MARKET_SPREAD_BELOW_COST: 'Piyasa fiyatına göre hesaplandı, ama sonuç alış fiyatımızın altında: bu otelde rekabetçi değiliz',
        MARGIN_RULE: 'Piyasa fiyatı gelmedi, marj kuralına göre hesaplandı',
      },

      /**
       * Yalnız `product.refreshable` true iken görünüyor (`kind === 'HOTEL' && supplierCode
       * === 'paximum'`); bu alan tek doğru kaynak, ekran kendi hesabını yapmıyor.
       */
      tazelemeAciklamasi:
        'Bu ürün gerçek bir Paximum sorgusu açabiliyor. Giriş/çıkış tarihi, yetişkin sayısı ve uyruk girip tazele.',
      tazelenemez:
        'Bu ürün türünde ya da tedarikçisinde canlı tazeleme çalışmıyor (yalnız Paximum otelleri). ' +
        'Buradaki fiyat en son bilinen tedarikçi sorgusuna dayanıyor, tedarikçiye şimdi gidilmiyor.',
      sogutmaNotu:
        'Art arda basmak tedarikçiye ikinci kez gitmeyebilir (beş dakikalık soğutma); ücret çıkarmaz ama daha taze bir sayı da getirmeyebilir.',
      girisTarihiEtiketi: 'Giriş tarihi',
      konaklamaTarihleriEtiketi: 'Giriş ve çıkış tarihi',
      cikisTarihiEtiketi: 'Çıkış tarihi',
      yetiskinEtiketi: 'Yetişkin sayısı',
      uyrukEtiketi: 'Uyruk (2 harf)',
      tazele: 'Tazele',
      tazelendi: 'Fiyat tazelendi.',
      tarihUyari: 'Giriş ve çıkış tarihini yazın.',
      yetiskinUyari: 'Yetişkin sayısı en az 1 olan bir tam sayı olmalı.',
      uyrukUyari: 'Uyruğu iki harfle yazın, örnek: TR.',
    },

    kalemlerBasligi: 'Teklif kalemleri',
    teklifToplamiBasligi: 'Teklif geneli',
    teklifIndirimiEtiketi: 'Teklif indirimi',
    teklifVergisiEtiketi: 'Teklif vergisi',
    teklifUcretiEtiketi: 'Teklif ücreti',
    teklifGecerlilikEtiketi: 'Teklifin geçerlilik tarihi',
    teklifGecerlilikGecmis: (bitis: string) => `Kayda yazılacak geçerlilik bitişi ${bitis}: bu an geçmişte. Tarih olduğu gibi korunur; teklif süresi dolmuş sayılır. Değiştirmek için yeni bir gün seçin.`,
    teklifGecerlilikBitis: (bitis: string) => `Kayda yazılacak geçerlilik bitişi: ${bitis}.`,
    teklifGecerlilikIpucu: 'Boş bırakırsanız teklif yayımlandığı andan itibaren 10 gün geçerli olur. Gün seçerseniz o günün sonuna kadar (Dubai saati) geçerlidir.',
    icNotlarEtiketi: 'İç notlar',
    musteriNotlariEtiketi: 'Müşteriye açık notlar',
    teklifOlustur: 'Teklif oluştur (sürüm 1)',
    teklifOlusturuldu: (kod: string, surum: string) => `Teklif oluşturuldu: ${kod}, sürüm ${surum}`,
    /**
     * Teklif notu uca tek alan olarak gidiyor ve iki bölümü bu önekler ayırıyor.
     * Önekler ekranda da okunuyor, bu yüzden Türkçe.
     */
    notBirlestir: (icNot: string, musteriNotu: string) => `İÇ NOT: ${icNot}\nMÜŞTERİ NOTU: ${musteriNotu}`,

    /**
     * Ekranda hazır gelen iki başlangıç kalemi. Tedarikçi adları özel ad, çevrilmiyor.
     *
     * Eskiden `Synthetic Hotels Ltd` / `Synthetic Tours Ltd` yazıyordu — test konsolundan kalma,
     * İngilizce ve uydurma. Fiyat ve adet bu iki kalemin (`tests/e2e/crm-a2-workflow.spec.ts`
     * `FIRST_QUOTE_MINOR`/`REVENUE_MINOR`) sabit varsaydığı değerler; onlar değişmiyor.
     */
    ornekKalemler: {
      otelAciklamasi: 'Boğaz konaklaması, 3 gece',
      otelTedarikcisi: 'Boğaz Otelcilik A.Ş.',
      turAciklamasi: 'Tarihi yarımada rehberli turu',
      turTedarikcisi: 'İstanbul Tur Rehberliği Ltd. Şti.',
    },
    kalemEksikUyari: 'Her teklif kalemi için en az iki karakterlik açıklama yazın.',

    surumBasligi: 'Teklif sürümleri',
    teklifSecin: 'Teklif seçin',
    surumEkle: 'Sonraki sürümü ekle',
    surumEklendi: (surum: string) => `Sürüm ${surum} eklendi; önceki sürümler değişmez kalıyor`,
    surumYayimla: 'Geçerli sürümü yayımla',
    /** Yayımla düğmesi pasifken görünen neden; durum bazlı. Yalnız taslak sürüm yayımlanır. */
    surumYayimlanamaz: (durum: string, durumAdi: string) =>
      durum === 'PUBLISHED' ? 'Bu sürüm zaten yayımda; yeniden yayımlamak gerekmiyor.'
        : durum === 'ACCEPTED' ? 'Bu sürüm müşteri tarafından kabul edildi; yeniden yayımlanamaz.'
          : `Yalnız taslak sürümler yayımlanabilir. Bu sürüm "${durumAdi}" durumunda.`,
    surumYayimlandi: (surum: string) => `Sürüm ${surum} yayımlandı`,
    kabulSurumuEtiketi: 'Kabul edilecek sürüm numarası',
    surumuKabulEt: 'Belirtilen sürümü kabul et',
    kabulYinelendi: (surum: string) => `Sürüm ${surum} için kabul yinelendi; ikinci bir kabul yazılmadı`,
    surumKabulEdildi: (surum: string) => `Sürüm ${surum} kabul edildi`,

    /**
     * Müşterinin "hayır" demesi.
     *
     * Ölçülen kusur: kabul düğmesi vardı, ret düğmesi yoktu. Kaybedilen bir teklif panelde
     * sonsuza kadar açık duruyordu — boru hattı, süresi dolmuş ve gerçekten kaybedilmiş teklifi
     * ayırt edemiyordu. Uç 22.08'den beri açıktı.
     *
     * Ret geri alınamaz: teklif ve sürüm reddedildi durumuna geçiyor ve durum makinesi oradan
     * geri bir yol tanımıyor. Onay kutusu bu yüzden var, ve gerekçe uca gidip denetim izine
     * yazılıyor.
     */
    teklifRet: {
      baslik: 'Müşteri teklifi reddetti',
      reddet: 'Teklifi reddedildi olarak kapat',
      /**
       * Sürüm numarası **sorulmuyor**: reddedilen şey müşterinin elindeki sürüm, yani geçerli
       * sürüm. Bir numara kutusu, satışçıya cevabını zaten bildiğimiz bir soru sormak ve yanlış
       * numara yazma ihtimalini açmak olurdu; hangi sürümün kapanacağı onay cümlesinde yazılı.
       */
      gerekceEtiketi: 'Müşterinin gerekçesi',
      gerekceIpucu: 'En az üç harf. Denetim izinde bu cümle okunuyor.',
      gerekceUyarisi: 'Ret gerekçesini yazın; en az üç harf.',
      onayCumlesi: (kod: string, surum: string) =>
        `${kod} teklifi ve ${surum} numaralı sürümü reddedildi olarak kapanacak. Kapanan teklif panelden yeniden açılamıyor; müşteri fikir değiştirirse yeni bir teklif gerekir.`,
      onayEtiketi: 'Okudum, teklifi kapatıyorum',
      onayUyarisi: 'Reddetmeden önce onay kutusunu işaretleyin.',
      reddedildi: (kod: string) => `${kod} teklifi reddedildi olarak kapandı.`,
      vazgec: 'Retten vazgeç',
    },

    /**
     * Satış akışı vakası madde 11-13: müşteri geçerlilik süresi (10 gün) içinde ödemezse teklif geçerliliğini
     * yitiriyor ama sepet kayıtlı kalıyor; "teklifi yenile" sepeti **güncel fiyatla** yeniden
     * proformalaştırıyor. Can alıcı kural: eski fiyat taşınmıyor, her kalem yeniden sorgulanıyor
     * — bu yüzden ekran değişimi (eski toplam / yeni toplam) açıkça gösteriyor, satışçı
     * müşteriye söyleyeceği rakamı görsün diye.
     */
    teklifiYenile: 'Teklifi yenile (güncel fiyatla)',
    teklifYenilendi: (surum: string) => `Sepet güncel fiyatla yeniden proformalaştı: sürüm ${surum}`,
    fiyatDegismedi: (toplam: string) => `Toplam değişmedi: ${toplam}`,
    fiyatDegisti: (eski: string, yeni: string) => `Toplam değişti: ${eski} idi, şimdi ${yeni}`,
    /** Elle yazılmış kalemler ve fiyatı yenilenemeyen kalemler için okunur uyarı listesi. */
    yenilemeUyarilariBasligi: 'Elle kontrol gerekiyor',

    /**
     * Satış akışı vakası madde 9: proforma ve MamoPay ödeme bağlantısı müşteriye gider. TRY
     * reddediliyor — ürün sahibinin kararı, ekran burada **umut vermiyor**: düğme her zaman
     * görünüyor ama basmadan önce neden çalışmayabileceği yazıyor, sessiz bir 409 beklenmiyor.
     * Karma sepet (otel + otel dışı kalem karışık) artık bir ret değil: Ürün Sahibi kararı
     * (25.08.2026) doğrudan tahsil. Satışçı bunu görsün ki müşteriye "paranız şimdi çekilecek"
     * diyebilsin — otel dışı zaten öyleydi, farkı olan otel kaleminin artık provizyona
     * gitmemesi.
     */
    odemeBaglantisiBasligi: 'Ödeme bağlantısı',
    odemeBaglantisiUret: 'MamoPay ödeme bağlantısı üret',
    baglantiUretildi: 'Ödeme bağlantısı üretildi.',
    baglantiZatenVardi: 'Bu sürüm için bağlantı zaten vardı; yeniden üretilmedi.',
    karmaSepetBilgisi:
      'Bu teklif otel ve otel dışı kalemi karışık taşıyor. Otel artık provizyonla gitmiyor: tüm tutar ödeme anında ' +
      'doğrudan tahsil edilecek. Müşteriye "paranız şimdi çekilecek" diyebilirsiniz. Otel konfirmasyonu gelmezse iade ' +
      'işleme konur.',
    tryUyarisi: 'Bu teklif TRY cinsinden. TRY tahsilatı yapılmıyor; ödeme bağlantısı üretilemeyecek.',
    yalnizYayimliSurumUyarisi: 'Ödeme bağlantısı yalnız yayımlanmış bir sürüm için üretilebilir; önce sürümü yayımlayın.',
    baglantiYinelemeRozeti: 'var olan bağlantı',

    /**
     * Vakanın 6-9. adımlarının ekran karşılığı: proforma, müşteri bağlantısı, WhatsApp.
     *
     * Dört uç 29.08.2026'ya kadar yazılmış ve sınanmıştı ama `web/` içinden **hiçbiri
     * çağrılmıyordu** (ölçüldü: `api.ts` içinde sıfır geçiş). Yani vakanın 6-9. adımları uçtan
     * yapılabiliyor, satışçının ekranından yapılamıyordu.
     *
     * Buradaki metinlerin ortak kuralı: **hiçbiri başarı vaat etmiyor.** WhatsApp sağlayıcısı
     * seçilmedi ve bu makinede hiçbir kimlik yok; düğme yine de duruyor, çünkü basıldığında
     * sunucunun reddi adıyla ve gerekçesiyle ekrana geliyor. Gerekçesini söyleyen bir düğme,
     * gizlenmiş bir düğmeden de sahte bir onaydan da doğru.
     */
    proformaIndir: 'Proforma PDF',
    proformaSurumIcin: (surum: string) => `Sürüm ${surum} proformasını indir`,
    proformaYayimSarti:
      'Proforma yalnız yayımlanmış ya da kabul edilmiş bir sürüm için üretilir; bu sürüm henüz taslak.',
    proformaIndirildi: (surum: string) => `Sürüm ${surum} proforması indirildi.`,

    musteriBaglantisiBasligi: 'Müşteri bağlantısı',
    musteriBaglantisiAcikla:
      'Bu adres oturum istemiyor: müşteri tarayıcısında doğrudan açar. Aynı adresin PDF hâli de var.',
    musteriBaglantisiAc: 'Müşterinin gördüğü sayfayı aç',
    musteriBaglantisiPdfAc: 'Müşterinin gördüğü PDF',
    musteriBaglantisiKopyala: 'Bağlantıyı kopyala',
    musteriBaglantisiKopyalandi: 'Müşteri bağlantısı panoya kopyalandı.',
    /**
     * Jeton yayımlama anında yazılıyor (`workflow.service.ts`); taslakta yok. Ölü bir bağlantı
     * göstermek yerine sebebi yazılıyor.
     */
    musteriBaglantisiYok:
      'Bu sürümün herkese açık bağlantı jetonu yok, bu yüzden müşteriye verilebilecek bir adres üretilemiyor. ' +
      'Jeton yayımlama anında oluşuyor; önce sürümü yayımlayın.',
    /** Sunucunun ürettiği mutlak adres geldiğinde panelin türettiği adresin yerine geçiyor. */
    musteriBaglantisiSunucudan: 'Bu adresi sunucu üretti; panelin türettiği adres yerine bu geçerli.',

    whatsappBasligi: 'WhatsApp gönderimi',
    /**
     * Basmadan önce söylenen tek cümle. "Deneyin, belki olur" demiyor: sağlayıcının seçilmediğini
     * ve sonucun ne olacağını baştan söylüyor.
     */
    whatsappSaglayiciNotu:
      'WhatsApp sağlayıcısı henüz seçilmedi ve bu kurulumda hiçbir kimlik tanımlı değil. Düğmeler yine de çalışıyor: ' +
      'sunucunun reddi adıyla ve gerekçesiyle aşağıda görünüyor, her deneme bir kayıt satırı bırakıyor.',
    whatsappProformaGonder: 'Proformayı ve ödeme bağlantısını WhatsApp’tan gönder',
    whatsappProformaGonderNotu:
      'Bu tuş önce MamoPay bağlantısını üretir (bu sürüm için bir tane vardıysa aynısını kullanır), sonra proformayla ' +
      'birlikte müşterinin numarasına göndermeyi dener. Gönderim reddedilse bile bağlantı üretilmiş olur ve kalıcıdır.',
    whatsappGitti: (saglayici: string) => `Mesaj ${saglayici} üzerinden gönderildi.`,
    whatsappGitmedi: (durum: string) => `Mesaj gönderilmedi: ${durum}`,
    whatsappKayitNo: (kayit: string) => `Gönderim kaydı: ${kayit}`,
    whatsappAlici: (numara: string) => `Alıcı: ${numara}`,
    whatsappProformaEklendi: (boyut: string) => `Proforma eklendi, ${boyut} bayt.`,
    whatsappProformaEklenmedi: 'Proforma eklenmedi.',
    whatsappOdemeTutari: (tutar: string) => `Ödeme tutarı: ${tutar}`,
    whatsappProvizyonlu: 'Provizyon: para yetkilendiriliyor, konfirmasyon gelince çekiliyor.',
    whatsappDogrudan: 'Doğrudan tahsil: para ödeme anında çekiliyor.',

    /** Genel gönderim katmanı (`POST /whatsapp/send`): fotoğraf, aktivite PDF'i, teklif metni. */
    whatsappSerbestBasligi: 'Tek mesaj gönder',
    whatsappSerbestAcikla:
      'Vakanın 6. ve 7. adımı: müşteri otel fotoğrafı ya da aktiviteler listesi istediğinde, ödeme bağlantısı ' +
      'üretmeden tek bir mesaj gönderilir.',
    whatsappTelefonEtiketi: 'Müşterinin WhatsApp numarası',
    whatsappAmacEtiketi: 'Gönderim amacı',
    whatsappMetinEtiketi: 'Mesaj metni',
    whatsappGonder: 'Mesajı gönder',
    whatsappBaglantiyiEkle: 'Müşteri bağlantısını metne ekle',
    whatsappTelefonBos: 'Gönderim için bir numara yazın.',
    whatsappMetinBos: 'Gönderilecek bir metin yazın.',

    siparisBasligi: 'Siparişe dönüştürme',
    sipariseDonustur: 'Kabul edilen teklifi siparişe dönüştür',
    siparisYinelendi: (kod: string) => `Var olan ${kod} siparişi döndü; ikinci bir sipariş oluşturulmadı`,
    siparisOlusturuldu: (kod: string, adet: string) => `${kod} siparişi ${adet} kalemle oluşturuldu`,
    yineleme: 'yineleme',
    olusturuldu: 'oluşturuldu',

    kapanisBasligi: 'Sipariş kapanışı',
    /**
     * Kural cümlesi durum adlarını dışarıdan alıyor: aynı kelimenin bir gün `DURUM_ADI` içinde
     * değişip burada eski hâliyle kalmasını engelliyor.
     */
    kapanisKurali: (tamamlandi: string, kapatildi: string) =>
      `Bir sipariş ${tamamlandi} durumuna ancak bütün görevleri ${tamamlandi} olduğunda geçiyor, ${kapatildi} durumuna ise ancak finans mutabakatı bittiğinde. Her adım uçta doğrulanan bir geçiş.`,
    siparisiTamamla: 'Siparişi tamamlandı olarak işaretle',
    siparisiKapat: 'Siparişi kapat',
    siparisGecti: (kod: string, durum: string) => `${kod} siparişi ${durum} durumuna geçti`,

    /**
     * FR-24: alt marj eşiğinin altında kalan sürüm yayımlanmıyor, müdürün onayını bekliyor.
     *
     * Ölçülen kusur: onay ucu 22.08'den beri açıktı ve panelde hiçbir düğmesi yoktu. Eşik altına
     * düşen bir teklif taslakta kalıyor ve kimse onu ileri götüremiyordu — satışçının yetkisi yok,
     * müdürün ekranı yok.
     *
     * Bölüm satış panelinin **dışında** duruyor: satış paneli teklif yazma yetkisiyle çiziliyor ve
     * müdürde o yetki yok. Onay bir satış yazma işi değil, ayrı bir karar.
     */
    marjOnayi: {
      ustBaslik: 'YÖNETİCİ ONAYI',
      baslik: 'Alt marj eşiği altındaki teklifler',
      tarif:
        'Marj eşiğinin altında kalan bir sürüm taslakta bekler ve yayımlanamaz. Onay o sürümü yayımlanabilir hâle getirir; fiyatı değiştirmez.',
      teklifSecin: 'Onaylanacak teklif',
      surumEtiketi: 'Onaylanacak sürüm numarası',
      notEtiketi: 'Onay notu',
      notIpucu: 'Denetim izinde bu cümle okunuyor.',
      onayla: 'Sürümü onayla',
      onaylandi: (kod: string, surum: string) => `${kod} teklifinin ${surum} numaralı sürümü onaylandı; artık yayımlanabilir.`,
      teklifYok: 'Onaya düşmüş bir teklif seçilmedi.',
      /** Eşik altında olup olmadığını uç biliyor; ekran tahmin etmiyor, reddi olduğu gibi yazıyor. */
      eslesmeNotu:
        'Bir sürümün eşik altında olup olmadığına uç karar verir. Onay gerekmeyen bir sürüm seçilirse gerekçesi burada yazılır.',
    },

    /**
     * Gelen talepler listesi.
     *
     * Ölçülen kusur: talep yazılıyordu, kaydediliyordu ve **bulunamıyordu**. Listeleme ucu
     * 27.08'den beri açıktı; panelde tek giriş, talebi o an oluşturan formdu. Sayfa yenilenince
     * talep gitmiş görünüyordu.
     *
     * Liste salt okunur ve bilerek öyle: düzeltme, onay ve fırsata bağlama adımları talep alma
     * formunun kendi işi ve o form teklif yazma yetkisi ister. Burada yapılan tek şey, yazılmış
     * bir talebi yeniden bulmak ve ne durumda olduğunu görmek.
     */
    talepListesi: {
      ustBaslik: 'GELEN TALEPLER',
      baslik: 'Talepler',
      tarif: 'Yazılmış her talep burada duruyor. Satır seçildiğinde alanları ve ayrıştırmanın sonucu okunur.',
      durumSuzgeci: 'Talep durumu',
      tumDurumlar: 'Tüm durumlar',
      bos: 'Hiç talep yazılmamış.',
      sutunOlusma: 'Yazıldığı an',
      sutunDurum: 'Durum',
      sutunDestinasyon: 'Destinasyon',
      sutunOtel: 'Otel',
      sutunGiris: 'Giriş',
      sutunKisi: 'Kişi',
      sutunAyristirma: 'Ayrıştırma',
      detay: 'Talebi aç',
      kapat: 'Talebi kapat',
      detayBasligi: 'Talebin alanları',
      hamMetinBasligi: 'Müşterinin yazdığı',
      alanYok: 'Bu talepte doldurulmuş alan yok.',
      /** Kimlik yazılmıyor: bir kimlik numarası bir ad değil ve ad bu uçtan hiç gelmiyor. */
      musteriBagi: 'Talep bir müşteri kaydına bağlandı.',
      musteriBagiYok: 'Talep henüz bir müşteri kaydına bağlanmadı.',
      firsatBagi: 'Talep bir fırsata bağlandı.',
      firsatBagiYok: 'Talepten henüz fırsat açılmadı.',
      /** Ayrıştırmanın dört hâli, ekranda ayrı ayrı: ikisi arıza, biri normal sonuç, biri başarı. */
      ayristirmaDurumu: {
        PENDING: 'Alanlar dolduruluyor',
        PARSED: 'Alanlar dolduruldu',
        PARSED_EMPTY: 'Metinde doldurulacak alan bulunamadı',
        FAILED: 'Ayrıştırma başarısız',
        NOT_CONFIGURED: 'Ayrıştırıcı tanımlı değil; alanlar elle dolduruluyor',
      } as Readonly<Record<string, string>>,
      sayfaBilgisi: (sayfa: string, toplam: string) => `${sayfa}. sayfa · ${toplam} kayıt`,
    },
  },

  operasyon: {
    baslik: 'Operasyon akışı',
    seciliSiparisBulunamadi: 'Seçilen sipariş yüklenen listede bulunmuyor. Siparişin kendi detay sayfasını açın veya listeden bir sipariş seçin.',
    tedarikciKanali: 'Tedarikçi kanalı: MANUAL_LOCAL · dış rezervasyon entegrasyonu NOT_CONFIGURED',

    /**
     * Görev kartının sırası: ekranda yazılı, düğmelerde işaretli.
     *
     * Ölçülen kusur: bir kartta on iki düğme, hepsi aynı yeşil, hiçbiri birincil işaretli
     * değil ve doğru sıra hiçbir yerde yazmıyordu. Düğmelerin diziliş sırasıyla gidildiğinde
     * on dört tık harcanıyor, ikisi hataya çarpıyordu — ve yolcu adının en başta girilmesi
     * gerektiği ancak beşinci tıkta öğreniliyordu.
     *
     * Üç şey değişti, üçü de aynı bilgiyi ekrana taşıyor:
     *
     *   1. Kartın üstünde tek cümlelik **sıradaki adım**.
     *   2. O adımın düğmesi birincil, geri kalanı ikincil.
     *   3. Henüz yapılamayan düğme **edilgen ve gerekçeli**: neden yapılamadığı düğmenin
     *      kendi üstünde yazıyor. Gizlemek daha kolay olurdu ve daha kötü olurdu: gizlenen
     *      düğme kullanıcıya "bu ekran bozuk" dedirtir, gerekçeli düğme sırayı öğretir.
     *
     * Sıra **uçtaki kuralın** aynısı (`workflow.service.ts` `transitionTask`/`issueVoucher`),
     * ekranın kendi uydurduğu bir sıra değil: bir gün kural değişirse iki yerde değişmesi
     * gerektiği buradan görünsün.
     */
    sonrakiAdimBasligi: 'Sıradaki adım',
    sonrakiAdimYok: 'Bu görevde yapılacak bir adım kalmadı.',
    /** Düğmenin edilgen olma gerekçeleri. Hepsi "ne yapmalı" diyor, kuralı tekrar etmiyor. */
    engel: {
      yolcuAdiYok: 'Önce yolcunun adını yazıp ayrıntıları kaydedin.',
      baslamadi: 'Önce görevi başlatın.',
      zatenBasladi: 'Görev zaten sürüyor.',
      kapandi: 'Görev kapandı; bu adım artık uygulanmıyor.',
      rezervasyonYok: 'Önce tedarikçi rezervasyonunu kaydedin.',
      rezervasyonTalepEdilmedi: 'Önce rezervasyonu tedarikçiden talep edin.',
      rezervasyonZatenTeyitli: 'Rezervasyon zaten teyit edildi.',
      rezervasyonTeyitBekliyor: 'Rezervasyon talep edilmeyi bekliyor.',
      teyitliRezervasyonYok: 'Önce tedarikçi rezervasyonunu referansla teyit edin.',
      voucherYok: "Önce bu kalemin voucher'ını düzenleyin.",
      voucherVar: "Bu kalemin voucher'ı zaten düzenlendi.",
      kontrolListesiAcik: 'Önce ayrıntıları kaydedip kontrol listesini kapatın.',
      notBos: 'Önce not kutusuna bir şey yazın.',
      belgeSecilmedi: 'Önce yüklenecek bir dosya seçin.',
    },
    /** Kartın etiketsiz değer yığınına ad veren etiketler. */
    kartEtiketleri: {
      kalem: 'Sipariş kalemi',
      durum: 'Durum',
      atanan: 'Atanan',
      sonTarih: 'Son tarih',
      basariImasi: 'Başarı iması',
      gerekce: 'Gerekçe',
      bekleyenKontrol: 'Bekleyen kontrol maddesi',
      rezervasyonDurumu: 'Rezervasyon durumu',
      rezervasyonReferansi: 'Tedarikçi referansı',
      voucher: 'Voucher',
    },
    csvIndir: 'Operasyon CSV dosyasını indir',
    csvUretildi: 'Operasyon CSV dosyası geçerli veritabanından üretildi',
    tumDurumlar: 'Tüm durumlar',
    durumFiltresiEtiketi: 'Görev durumu filtresi',
    atananEtiketi: 'Atanacak kullanıcı kimliği',
    atananIpucu: 'Kullanıcı kimliği; kişi seçen bir liste henüz yok.',
    sonTarihEtiketi: 'Son tarih',
    sonTarihYok: 'Son tarih yok',
    oncelikSatiri: (oncelik: string) => `Öncelik: ${oncelik}`,
    oncelikEtiketi: 'Öncelik',
    oncelikKaydet: 'Önceliği ve son tarihi kaydet',
    oncelikKaydedildi: (kod: string) => `${kod} görevinin önceliği ve son tarihi kaydedildi`,
    atamaYap: 'Ata ve son tarihi belirle',
    atandi: (kod: string) => `${kod} görevi atandı`,
    basariImaEdilmiyor: 'başarı ima edilmiyor',
    suruyor: 'sürüyor',
    yolcuAdiEtiketi: 'Yolcunun adı',
    yolcuAdiIpucu: "Voucher'a ve tedarikçiye bu ad gidiyor.",
    ayrintilariKaydet: 'Ayrıntıları kaydet ve kontrol listesini tamamla',
    ayrintilarKaydedildi: (kod: string) => `${kod} görevinin zorunlu ayrıntıları ve kontrol listesi kaydedildi`,
    kontrolListesiNotu: 'operasyon teyit etti',

    notlarBasligi: 'Operasyon notları',
    notSatiri: (govde: string, yazar: string) => `${govde} · ${yazar}`,
    notYok: 'Henüz operasyon notu yok.',
    yeniNotEtiketi: 'Yeni operasyon notu',
    notIpucu: 'Devir için olgusal bağlam yazın',
    notEkle: 'Not ekle',
    notBos: 'Bir operasyon notu yazın.',
    notKaydedildi: 'Operasyon notu yazarı ve zaman damgasıyla kaydedildi',

    belgelerBasligi: 'Görev belgeleri',
    belgeIndirildi: (dosya: string) => `${dosya} indirildi`,
    belgeBoyutu: (bayt: string) => `${bayt} bayt`,
    belgeYok: 'Yüklenmiş belge yok.',
    belgeSecEtiketi: 'Göreve eklenecek belge',
    belgeYukle: 'Belge yükle',
    belgeSecUyari: 'Önce bir belge seçin.',
    belgeOkunamadi: 'Seçilen belge okunamadı.',
    belgeYuklendi: (dosya: string, kod: string) => `${dosya} yüklendi ve ${kod} görevine eklendi`,

    /**
     * Vize destek belgesi üretimi (alt modül).
     *
     * Uç 10.09.2026'da hazırdı ve hiçbir yerden çağrılmıyordu: devir belgesinin açık kalanlar
     * listesinde birinci madde buydu, "ekip kendi başına belge basamıyor". Alan görev kartının
     * içinde, belge listesinin hemen yanında duruyor, çünkü üretilen PDF o listeye düşüyor.
     *
     * Alanların hepsi isteğe bağlı görünüyor ama ikisi değil: yolcu adı ve otel ya da rota
     * seçimi. Ekran ikisini de düğmeyi edilgen bırakarak söylüyor, uç ise kendi denetimini
     * yine yapıyor. Otel 02.11.2026'dan beri MBD otel kataloğundan (v3, yayındaki oteller)
     * aranıp seçilir; katalogda olmayan alan "bilgi eksik" diye gösterilir, uydurulmaz.
     */
    vizeBelge: {
      baslik: 'Vize belgesi üret',
      aciklama: 'Konsolosluğa sunulan otel rezervasyon onayı ya da uçak bileti. Üretilen belge görev belgelerine düşer.',
      belgeTuruEtiketi: 'Belge türü',
      belgeTuruAdi: {
        otel: 'Otel rezervasyon onayı',
        ucak: 'Uçak bileti',
        otel_ucak: 'Uçak bileti + otel rezervasyonu',
      },
      birlesikIpucu: 'İki ayrı belge üretilir: otel rezervasyon onayı ve uçak bileti. İkisi de gidiş ve dönüş tarihlerini kullanır.',
      tarihAraligiEtiketi: 'Gidiş ve dönüş tarihleri',
      hazirOtelEtiketi: 'Otel',
      hazirOtelSecilmedi: 'Listeden seçin ya da otel adı yazın',
      otelAraIpucu: 'Altı hazır otel her zaman listededir; kataloğun yayındaki otelleri için en az iki harf yazın.',
      otelAraniyor: 'Oteller aranıyor',
      otelBulunamadi: 'Katalogda bu adla yayında otel yok. Başka bir yazım deneyin ya da Başka otel ile elle girin.',
      otelAramaHatasi: 'Otel kataloğuna ulaşılamadı.',
      otelDetayHatasi: 'Otel bilgisi alınamadı.',
      bilgiEksik: 'Bilgi eksik',
      fotografEksik: 'Bu otelin kayıtlı fotoğrafı yok; belge fotoğrafsız basılır.',
      telefonKaynakYok: 'Katalogda otel telefonu yok: bilgi eksik, elle yazın.',
      katalogYanitsiz: 'MBD otel kataloğuna şu an ulaşılamıyor; yalnız hazır oteller listeleniyor. Başka otel için Başka otel ile elle girin.',
      hazirOtelEtiketiKisa: 'hazır',
      katalogaDon: 'Kayıtlı otellerden seç',
      baskaOtel: 'Başka otel',
      kayitliOtelIpucu: 'Otelin kayıtlı bilgileri otomatik kullanılır.',
      ekSecenekler: 'Ek seçenekler (isteğe bağlı)',
      geceOzeti: (gece: number) => `${gece} gece · Gidiş ve dönüş tarihlerinden hesaplandı.`,
      otelAdiEtiketi: 'Otel adı (elle)',
      otelAdiIpucu: 'Hazır listede yoksa adı, şehri, adresi, telefonu ve koordinatı birlikte yazın.',
      otelSehirEtiketi: 'Otel şehri',
      otelAdresEtiketi: 'Otel adresi',
      otelTelefonEtiketi: 'Otel telefonu',
      otelGpsEtiketi: 'Otel koordinatı',
      otelGpsIpucu: 'Örnek: 25.185619, 55.271606. Belgedeki haritayı bu koordinat çiziyor.',
      odaTipiEtiketi: 'Oda tipi',
      pansiyonEtiketi: 'Pansiyon',
      geceEtiketi: 'Gece sayısı',
      odaEtiketi: 'Oda sayısı',
      girisEtiketi: 'Giriş tarihi',
      girisIpucu: 'Boş bırakılırsa sipariş kaleminin hizmet tarihi yazılır.',
      yolcularEtiketi: 'Yolcu adları',
      yolcularIpucu: 'Ad ve soyadları her satıra bir kişi olacak şekilde yazın veya virgülle ayırın.',
      kisiSayisiEtiketi: 'Kişi dökümü',
      kisiSayisiIpucu: 'Örnek: 2 Adults, 1 Child',
      rotaEtiketi: 'Uçuş rotası',
      rotaSecilmedi: 'Seçilmedi',
      rotaAdi: {
        dubai: 'Dubai',
        dubai_gece: 'Dubai, gece uçuşu',
        sarjah: 'Şarja',
      },
      ucusTipiEtiketi: 'Uçuş tipi',
      ucusTipiAdi: {
        roundtrip: 'Gidiş dönüş',
        oneway: 'Tek yön',
      },
      gidisTarihEtiketi: 'Gidiş tarihi',
      donusTarihEtiketi: 'Dönüş tarihi',
      koltukEtiketi: 'İlk yolcunun koltuğu',
      koltukIpucu: 'Örnek: 12A. Kalan koltuklar sırayla dağıtılıyor.',
      notEtiketi: 'Belge kaydına düşülecek not',
      notIpucu: 'Belgeye basılmaz, yalnız belge kaydında durur.',
      uret: 'Vize belgesi üret',
      uretiliyor: 'Vize belgesi üretiliyor',
      uretildi: (dosya: string, kod: string) => `${dosya} üretildi ve ${kod} görevinin belgelerine eklendi`,
      uretildiCoklu: (dosyalar: string, kod: string) => `${dosyalar} üretildi ve ${kod} görevinin belgelerine eklendi`,
      /** Uç 400 döndüğünde ekranın kendi tanıdığı anahtarlar da yazılıyor. */
      gecerliRotaAnahtarlari: (liste: string) => `Ekranın tanıdığı uçuş rotaları: ${liste}`,
      engel: {
        yetkiYok: 'Bu alanı görürsünüz, işlemezsiniz: belge üretimi document:write yetkisi istiyor.',
        yolcuAdiYok: 'Önce yolcu adlarını yazın; belge isimsiz kesilmiyor.',
        otelSecilmedi: 'Önce kayıtlı otellerden bir otel seçin ya da otel adını elle yazın.',
        otelAyrintiEksik: 'Kişi dökümü, oda sayısı, oda tipi ve pansiyon zorunlu: otel belgesi bunlar olmadan kesilmez.',
        otelTelefonYok: 'Otel telefonunu yazın: katalogda otel telefonu yok ve belgeye uydurma numara basılmaz.',
        katalogAdresYok: 'Katalogda bu otelin adresi ya da koordinatı yok: eksik alanı elle yazın.',
        rotaSecilmedi: 'Önce uçuş rotasını seçin.',
        tarihYok: 'Gidiş ve dönüş tarihlerini seçin. Tek yön uçuşta yalnız gidiş tarihi gerekir.',
        tarihSirasi: 'Dönüş, gidişten önce olamaz. Otel konaklaması en az 1 gece olmalıdır.',
        geceSiniri: 'Otel belgesi en fazla 60 gecelik konaklama destekliyor.',
        uretimSuruyor: 'Bir belge üretimi sürüyor; bitmesini bekleyin.',
      },
    },

    /**
     * "Belge üretici": siparişten ve görevden bağımsız vize belgesi (10.09.2026).
     *
     * Ürün Sahibi: *"Sipariş şu anda WhatsApp'tan geliyor; CRM'in içinden belgeyi siparişsiz
     * üretebilmem lazım. Oraya belge üretici gibi konumlandır, tam entegrasyonu sonra yaparız."*
     *
     * Ekranda ne olduğu yazılı duruyor: bu belge hiçbir siparişe bağlanmıyor. Yazmasaydı,
     * buradan basılan bir belgenin sipariş kaydında görüneceği sanılırdı.
     */
    serbestBelge: {
      baslik: 'Belge üretici',
      aciklama: 'Siparişten ve görevden bağımsız vize destek belgesi. WhatsApp\'tan gelen talepte CRM kaydı beklenmeden basılır.',
      uyari: 'Buradan basılan belge hiçbir siparişe bağlanmaz. Sipariş kaydı varsa belgeyi görev kartından basın.',
      musteriEtiketi: 'Müşteri kaydı (isteğe bağlı)',
      musteriIpucu: 'Kayıt açılmışsa belge o müşteriye bağlanır. Boş bırakılabilir.',
      musteriSecilmedi: 'Bağlanmadı',
      uret: 'Belgeyi üret',
      uretiliyor: 'Belge üretiliyor',
      uretildi: (dosya: string) => `${dosya} üretildi`,
      uretildiCoklu: (dosyalar: string) => `${dosyalar} üretildi`,
      indir: 'İndir',
      sonUretilenler: 'Son üretilen serbest belgeler',
      yok: 'Siparişsiz üretilmiş belge yok.',
      yenile: 'Listeyi yenile',
      yenilendi: 'Serbest belge listesi tazelendi',
      /**
       * Sayfalama (11.09.2026, Operasyon v0.2). Ürün Sahibi: *"Üretilen belgelere pagination
       * yapılsın, aşağa doğru uzar gider yoksa."* Biçim müşteri listesiyle aynı:
       * "42 belge · Sayfa 1/3". Sayfa boyu 20, uç tavanı 100.
       */
      sayfaEki: 'belge',
      sayfaBoyu: 20,
      musteriyeBagli: (musteri: string) => `müşteri: ${musteri}`,
      musteriyeBagliDegil: 'müşteriye bağlı değil',
      satirAyrinti: (tur: string, tarih: string, bag: string) => `${tur} · ${tarih} · ${bag}`,
    },

    /**
     * Operasyon bölümünün "Vize Belgeleri" alt görünümü.
     *
     * Ayrı bir bileşen ve ayrı bir izinle çiziliyor (`document:read`), çünkü görev kartlarını
     * taşıyan panelin kapısı `fulfilment:write` ve ADMIN o yetkiyi tutmuyor. Kural değişmedi:
     * ADMIN görür, işlemez. Burada gördüğü şey üretilmiş belgelerin listesi, üretim
     * düğmesi değil.
     */
    vizeBelgeleri: {
      baslik: 'Vize Belgeleri',
      aciklama: 'Seçilen siparişte üretilmiş vize destek belgeleri, en yenisi üstte.',
      siparisEtiketi: 'Sipariş',
      yok: 'Bu siparişte üretilmiş vize belgesi yok.',
      turAdi: {
        VIZE_OTEL_VOUCHER: 'Otel rezervasyon onayı',
        VIZE_UCAK_BILETI: 'Uçak bileti',
      },
      satirAyrinti: (gorev: string, tur: string, tarih: string) => `${gorev} · ${tur} · ${tarih}`,
      saltOkunur: 'Belgeleri görürsünüz, üretmezsiniz. Üretim görev kartında ve document:write yetkisi istiyor.',
      /**
       * Liste kendiliğinden tazelenmiyor: üretim yukarıdaki görev kartında, bu görünüm ise ayrı
       * bir bileşen ve ayrı bir istekle doluyor. Sessizce bayat kalmaktansa tazeleme düğmesi
       * konuyor, çünkü bayat listeyi kullanıcı ancak eksik bir belgeyi ararken fark ederdi.
       */
      yenile: 'Listeyi yenile',
      yenilendi: 'Vize belgeleri listesi tazelendi',
      indirildi: (dosya: string) => `${dosya} indirildi`,
    },

    basla: 'Başlat',
    baslatildi: (kod: string) => `${kod} görevi sürüyor`,
    gerekceEtiketi: 'Bloke gerekçesi',
    gerekceIpucu: 'gerekçe',
    blokeEt: 'Bloke et',
    blokeEdildi: (kod: string) => `${kod} görevi bloke edildi; bu bir başarı durumu değil`,

    rezervasyonKaydet: 'Tedarikçi rezervasyonu kaydet',
    rezervasyonNotu: 'elle kaydedildi; tanımlı tedarikçi entegrasyonu yok',
    rezervasyonKaydedildi: (durum: string) => `Tedarikçi rezervasyonu kaydedildi (${durum})`,
    referansEtiketi: 'Tedarikçi referansı',
    referansIpucu: 'tedarikçi referansı',
    rezervasyonUyari: 'Önce bir rezervasyon kaydedin.',
    talepEt: 'Talep et',
    talepEdildi: 'Rezervasyon tedarikçiden elle talep edildi',
    referansIleTeyitEt: 'Referansla teyit et',
    teyitKaydedildi: 'Operatörün kaydettiği tedarikçi teyidi saklandı; bu bir entegrasyon yanıtı değil',

    voucherDuzenle: 'Voucher düzenle',
    /** Voucher gerçek bir yolcu adı taşımadan düzenlenemez; uydurma bir ad yazılmıyor. */
    voucherYolcuUyarisi: 'Voucher düzenlemeden önce yolcunun adını yazın.',
    voucherYonergesi: 'bu voucher tedarikçi bankosunda gösterilecek',
    voucherDuzenlendi: (kod: string) => `${kod} voucher'ı yalnız bu kalem için düzenlendi`,

    tamamlandiIsaretle: 'Tamamlandı olarak işaretle',
    tamamlandi: (kod: string) => `${kod} görevi tamamlandı`,
  },

  /**
   * CRM denetimi B6: tedarikçi iptal kuyruğu (`operations/supplier-cancellations`). Ürün Sahibi,
   * 21.08.2026: "GlobDMC iptalleri direkt yansımasın, biz operasyonda görelim, müşteri aranıp
   * farklı turla değiştirilebilir ama kabul etmezse mecbur iptal." Kayıt açıldığında hiçbir şey
   * değişmiyor; GeziPuan yalnız CANCELLED kararında geri alınıyor.
   */
  tedarikciIptali: {
    baslik: 'Tedarikçi iptalleri',
    aciklama: 'Tedarikçiden gelen bir iptal önce burada açılır; müşteri aranana kadar hiçbir şey değişmez.',
    durumFiltresi: 'Durum',
    tumu: 'Tümü',
    listeYok: 'Bu durumda bekleyen tedarikçi iptali yok.',
    sutun: { siparis: 'Sipariş', tedarikci: 'Tedarikçi', neden: 'Neden', bildirilme: 'Bildirilme', durum: 'Durum' },
    yeniBasligi: 'Yeni iptal bildir',
    alan: {
      siparisReferansi: 'Sipariş referansı',
      /** Site tarafındaki müşteri kimliği. İç sürüm numarası (`v3`) ekranda geçmiyor. */
      musteriId: 'Müşteri kimliği (web sitesi)',
      tedarikciKodu: 'Tedarikçi kodu',
      urunId: 'Ürün kimliği (opsiyonel)',
      kalkisId: 'Kalkış kimliği (opsiyonel)',
      kalkisTarihi: 'Kalkış tarihi (opsiyonel)',
      neden: 'Tedarikçinin söylediği, olduğu gibi',
    },
    bildir: 'İptali bildir',
    bildirildi: (referans: string) => `${referans} siparişi için iptal kaydı açıldı`,
    kararBasligi: 'Kararı kapat',
    sonuc: 'Sonuç',
    ikameEdildi: 'Başka turla değiştirildi (SUBSTITUTED)',
    iptalEdildi: 'Müşteri kabul etmedi, iptal (CANCELLED)',
    ikameSiparis: 'Yerine geçen sipariş referansı',
    kararNotu: 'Görüşmede ne konuşuldu',
    kararVer: 'Kararı kaydet',
    kararVerildi: (referans: string) => `${referans} siparişi kapatıldı`,
    gezipuanGeriAlindi: (tutar: string) => `GeziPuan geri alındı: ${tutar}`,
    gezipuanKarsilanamadi: (tutar: string) => `Müşteri harcamış, karşılanamayan tutar: ${tutar}`,
  },

  /** CRM denetimi B7: vize talebi kuyruğu (`operations/visa-requests`). v3 açar, operatör kapatır. */
  vizeTalebi: {
    baslik: 'Vize talepleri',
    aciklama: 'Ödenmiş bir vize talebi burada açılır; Arkan\'a gönderilip sonuç kaydedilene kadar bekler.',
    durumFiltresi: 'Durum',
    tumDurumlar: 'Tüm durumlar',
    siparisFiltresi: (reference: string) => `Sipariş: ${reference}`,
    tumBasvurular: 'Tüm başvuruları göster',
    listeYok: 'Bu durumda bekleyen vize talebi yok.',
    sutun: { siparis: 'Sipariş', basvuran: 'Başvuran', urun: 'Ürün', durum: 'Durum', alindi: 'Alınma' },
    detay: {
      basvuranTuru: 'Başvuran türü',
      listeFiyati: 'Liste fiyatı',
      arkanNumarasi: 'Arkan referans numarası',
      gonderildi: 'Arkan\'a gönderilme',
      sonucNotu: 'Karar notu',
      maliyetBasligi: 'Maliyet (Arkan)',
      maliyetGizli: 'Maliyet finance:read yetkisi istiyor',
      beklenen: 'Beklenen',
      taahhut: 'Taahhüt edilen',
      gerceklesen: 'Gerçekleşen',
      fark: 'Fark (taahhüt - beklenen)',
    },
    gonder: 'Arkan\'a gönderildi olarak işaretle',
    gonderildi: (referans: string) => `${referans} Arkan'a gönderildi işaretlendi`,
    kararBasligi: 'Arkan\'ın cevabını kaydet',
    verildi: 'Verildi (ISSUED)',
    reddedildi: 'Reddedildi (REJECTED)',
    supplierReferansi: 'Arkan numarası (ISSUED\'da zorunlu)',
    ucretlendirilen: 'Arkan\'ın faturaladığı tutar (opsiyonel, kuruş)',
    kararNotu: 'Not',
    kararVer: 'Kararı kaydet',
    kararVerildi: (referans: string) => `${referans} kararı kaydedildi`,
    ekUcretBasligi: 'Sözleşme dışı ücret ekle (iptal, uzatma, gecikme...)',
    ucretKodu: 'Ücret kodu (Arkan fiyat listesinden)',
    adet: 'Adet',
    tutarEzme: 'Tutarı ez (opsiyonel, kuruş — boşsa sözleşme fiyatı kullanılır)',
    ekUcretEkle: 'Ücreti kaydet',
    ekUcretEklendi: (kod: string, tutar: string) => `${kod} ücreti eklendi: ${tutar}`,
  },

  /**
   * CRM denetimi B8: `operations/service-completions`. Yalnız bitiş tarihi olmayan üründe gerekli
   * — tarihli bir tur/otel kendi dönüş tarihinde kendiliğinden olgunlaşır. Liste ucu yok: hangi
   * siparişin bunu beklediğini söyleyen ayrı bir kuyruk yok, bu yüzden sipariş referansı elle
   * girilir.
   */
  hizmetTamamlama: {
    baslik: 'Hizmet tamamlama (bitiş tarihi olmayan ürün)',
    aciklama: 'Yalnız tarihsiz bir ürün için gerekli: GeziPuan\'ı burada olgunlaştırır.',
    siparisReferansi: 'Sipariş referansı',
    tamamlanmaTarihi: 'Tamamlanma tarihi (opsiyonel, boşsa bugün)',
    not: 'Not (opsiyonel)',
    kaydet: 'Tamamlandı olarak kaydet',
    olgunlastiEvet: (tutar: string) => `GeziPuan olgunlaştı: ${tutar}`,
    olgunlastiHayirNeden: {
      NO_CREDIT: 'Bu siparişin GeziPuan kazancı yok.',
      ALREADY_MATURED: 'Bu kredi zaten harcanabilir durumda.',
      CLAWED_BACK: 'Bu kredi geri alınmış.',
      COMPLETION_BEFORE_PAYMENT: 'Tamamlanma tarihi ödeme tarihinden önce olamaz.',
    },
  },

  /** Satış akışı vakası madde 16: operasyon takvimi — siparişler arası, tarihe göre çapraz görünüm. */
  operasyonTakvimi: {
    baslik: 'Operasyon takvimi',
    aciklama:
      'Bugünden başlayan bir pencere: hangi otel gecesi, hangi tur günü, hangi saatli aktivite ' +
      'var. Tarihsiz kalemler (ör. vize) bu pencerenin dışında ama her zaman aşağıda, ayrı ' +
      'listede — bir tarih aralığı seçmek onları kaybetmenin gerekçesi değil.',
    baslangic: 'Başlangıç',
    bitis: 'Bitiş',
    uygula: 'Uygula',
    varsayilanPencere: 'Tarih verilmezse bugünden başlayan 30 gün gösterilir.',
    sutunSiparis: 'Sipariş',
    sutunMusteri: 'Müşteri',
    sutunKalem: 'Kalem',
    sutunTedarikci: 'Tedarikçi',
    sutunTur: 'Tür',
    sutunZaman: 'Zaman',
    sutunGorev: 'Görev durumu',
    tur: {
      RANGE: 'Tarih aralığı',
      DAY: 'Tek gün',
      TIMED: 'Saatli',
      UNDATED: 'Tarihsiz',
    } as Readonly<Record<string, string>>,
    bosTarihli: 'Bu pencerede tarihli kalem yok.',
    undatedBasligi: (adet: string) => `Tarihsiz kalemler (${adet}) — hiçbir pencerede kaybolmaz`,
    undatedYok: 'Tarihsiz kalem yok.',
    hata: 'Takvim okunamadı.',
  },

  /** Satış akışı vakası madde 17: "tedarikçilere bildirimi yapar." */
  tedarikciBildirimi: {
    baslik: 'Tedarikçi bildirimi',
    aciklama:
      'Gerçek bir gönderim kanalı yok (WhatsApp sağlayıcısı seçilmedi). Bu, boş bir düğme ' +
      'değil: operatör tedarikçiye telefonla/e-postayla kendisi ulaştığında burada kaydeder.',
    kanalYapilandirilmamis: 'Otomatik gönderim: yapılandırılmamış. Aşağıdaki her kalem için elle ulaşıp kaydedin.',
    durumFiltresi: 'Durum',
    tumDurumlar: 'Tümü',
    beklemede: 'Bekliyor',
    gonderildi: 'Ulaşıldı',
    sutunKalem: 'Kalem',
    sutunTedarikci: 'Tedarikçi',
    sutunDurum: 'Durum',
    sutunUlasilmaAni: 'Ulaşılma anı',
    sutunUlasan: 'Ulaşan',
    bos: 'Bu süzgeçte hiçbir kalem yok.',
    ulasildiIsaretle: 'Telefonla/e-postayla ulaştım',
    notEtiketi: 'Not (isteğe bağlı)',
    kaydedildi: 'Tedarikçiye ulaşıldığı kaydedildi.',
    hata: 'Tedarikçi bildirim listesi okunamadı.',
    surumUyusmuyor: 'Bu kayıt başka bir yerde değişti. Listeyi yenileyip tekrar deneyin.',
  },

  /**
   * Satış akışı vakası madde 18: "hangi tedarikçiye ne ödeneceğini girer." 21.08.2026 kararı:
   * operatör maliyeti **yazar, geri okumaz** — ekranda karşılaştırma ya da "beklenen tutar"
   * ipucu yok, kasıtlı.
   */
  tedarikciMaliyeti: {
    baslik: 'Tedarikçiye ödenecek tutar',
    aciklama:
      'Tedarikçiden telefonda öğrendiğiniz rakamı buraya girin. Bu ekran hiçbir tutarı size geri ' +
      'göstermez — ne bilinen bir referans, ne az önce girdiğiniz kayıt. Kayıt Finans\'a gider.',
    kalemSec: 'Kalem',
    kalemSecin: 'Bir kalem seçin',
    durumBekliyor: 'Bu kalem için tedarikçi maliyeti henüz girilmedi.',
    durumGirilmis: (tarih: string) => `Bu kalem için maliyet girildi (son giriş: ${tarih}).`,
    tutar: 'Tutar',
    paraBirimi: 'Para birimi',
    not: 'Not (isteğe bağlı)',
    kaydet: 'Tutarı kaydet',
    kaydedildi: 'Tedarikçiye ödenecek tutar kaydedildi ve Finans\'a geçti.',
    hata: 'Kalem seçilemedi ya da durum okunamadı.',
  },

  finans: {
    baslik: 'Finans akışı',
    /**
     * CRM eksik denetimi (26.08.2026) madde Y3: bu satır eskiden sabit bir cümleydi, sipariş ne
     * olursa olsun aynı metni gösteriyordu. Artık `summary.invoice.note`u basıyor — backend'in
     * `assessInvoiceReadiness` ile sipariş başına gerçekten hesapladığı durum. Bu değişken yalnız
     * özet hiç yüklenmemişken (ilk render) yer tutucu.
     */
    faturaDurumu: 'Fatura durumu yükleniyor…',
    faturaEksikDegiskenler: (adlar: string[]) => `Eksik ortam değişkenleri: ${adlar.join(', ')}`,

    /**
     * Faturanın iki adımı, **sırasıyla**.
     *
     * Ölçülen kusur: iki uç da 22.08'den beri açıktı ve panelde ikisinin de düğmesi yoktu. Finans
     * yalnız kesilmiş faturaların listesini görüyordu ve o liste hiç dolmuyordu, çünkü kesme
     * çağrısını yapan bir ekran yoktu.
     *
     * Sıra ekranda **yazılı**, denenip 409 alınarak öğrenilen bir şey değil: sipariş fatura para
     * biriminden başka bir para birimindeyse önce kur donduruluyor, sonra fatura kesiliyor. Kesme
     * düğmesi uç hazır demeden basılamıyor ve gerekçesini üstünde taşıyor.
     */
    fatura: {
      baslik: 'Fatura',
      adimKur: '1. Fatura kurunu dondur',
      adimKes: '2. Faturayı kes',
      /**
       * Birinci adım iki yoldan tamamlanıyor: kur donduruldu, ya da sipariş zaten fatura para
       * biriminde olduğu için çevrim hiç gerekmedi. Ekran ikisini **ayırmıyor**, çünkü ayırt
       * edecek veriyi uç göndermiyor ve tahmin etmek uydurmak olurdu. Sonucu ikisinde de aynı:
       * bu adım fatura kesmenin önünde durmuyor.
       */
      kurTamam: 'Fatura kuru bu sipariş için hazır; bu adım fatura kesmenin önünde durmuyor.',
      kurTekSefer:
        'Kur bir kez donuyor ve üzerine yazılmıyor. Yanlış bir kurun karşılığı bu ekranda bir düzeltme değil, ayrı bir karardır.',
      kurEtiketi: 'Bir birim sipariş parası kaç fatura parası',
      kurIpucu: 'En çok altı ondalık basamak. Örnek: 3,672500',
      kurBicimiUyarisi: 'Kuru yazarken en çok altı ondalık basamak kullanın. Örnek: 3,672500',
      kurKaynagiEtiketi: 'Kurun kaynağı',
      kurTarihiEtiketi: 'Kurun ait olduğu gün',
      kurTarihiIpucu: 'Paranın geldiği gün, faturanın kesildiği gün değil.',
      kurTarihiUyarisi: 'Kurun ait olduğu günü seçin.',
      kaynakAdi: {
        MAMOPAY_SETTLEMENT: 'Tahsilat mutabakatı',
        BANK_STATEMENT: 'Banka ekstresi',
        MANUAL_LOCAL: 'Elle girildi',
      } as Readonly<Record<string, string>>,
      kuruDondur: 'Kuru dondur',
      /**
       * Bildirim çevrilmiş **tutarı yazmıyor**. Uç `invoiceAmountMinor` döndürüyor ama o tutar
       * fatura para biriminde ve ekranın elinde o para biriminin adı yok; sipariş para biriminin
       * simgesiyle yazmak yanlış bir rakam göstermek olurdu. Tutar, kesilen faturanın kendi
       * satırında (uçtan gelen para birimiyle birlikte) okunuyor.
       */
      kurYazildi: 'Kur donduruldu. Fatura tutarı bu kurla hesaplanacak.',
      kurZatenYazildi: 'Aynı kur zaten donmuştu; ikinci bir kayıt yazılmadı.',

      hazir: 'Fatura için gereken her koşul sağlandı.',
      hazirDegilBaslik: 'Fatura kesilemiyor. Eksikler:',
      hazirDegilIpucu: 'Faturayı CRM kesmez: Finans PayPartner\'da keser, sonra kaydı Finans ekranından girer.',
      saglayiciYok: 'Fatura sağlayıcısı tanımlı olmadığı için kesme çağrısı yapılamaz.',
      kosulSaglandi: 'sağlandı',
      kosulEksik: 'eksik',
      /**
       * Koşul adları. Anahtar uçtan geliyor; uçtaki açıklama cümlesi **çizilmiyor**, çünkü o cümle
       * alan adları ve uç adresleriyle yazılmış ve bu ekranın dili operatörün dili.
       */
      kosulAdi: {
        odemenin_tamami: 'Ödemenin tamamı doğrulandı',
        maliyet_belli: 'Tedarikçi maliyeti belli',
        siparis_acik: 'Sipariş iptal edilmemiş',
        tedarikci_rezervasyonu_onayli: 'Tedarikçi rezervasyonu onaylı',
        fazla_odeme_yok: 'Fazla ödeme yok',
        musteri: 'Müşteri adı',
        lisansnumarasi: 'Kimlik ya da vergi numarası',
        eposta: 'Müşteri e-posta adresi',
        telefonno: 'Müşteri telefon numarası',
        adres: 'Fatura adresi',
        uyruk: 'Müşterinin ülkesi',
        parabirimi: 'Fatura kuru donduruldu',
        fatno: 'Fatura numarası',
        paypartner_fatura_id: 'Fatura eşleme kaydı',
      } as Readonly<Record<string, string>>,
      /** Ölçülüyor ama faturayı durdurmuyor; sessizce eksik göstermek yanlış olurdu. */
      durdurmayanKosul: 'Faturayı durdurmuyor',
    },

    /**
     * Kanıtı reddetmek: doğrulamanın karşı yarısı.
     *
     * Ölçülen kusur: "Doğrula" düğmesi vardı, "Reddet" düğmesi yoktu. Tutmayan bir dekontla
     * karşılaşan Finans kullanıcısının tek seçeneği kanıtı kuyrukta bırakmaktı, ve kuyrukta kalan
     * bir kanıt bir gün yanlışlıkla doğrulanır.
     */
    kanitRet: {
      reddet: 'Kanıtı reddet',
      gerekceEtiketi: 'Ret gerekçesi',
      gerekceIpucu: 'En az üç harf. Denetim izinde bu cümle okunuyor.',
      gerekceUyarisi: 'Ret gerekçesini yazın; en az üç harf.',
      onayCumlesi: (tur: string, tutar: string) =>
        `${tur} kanıtı ${tutar} reddedilecek. Reddedilen kanıt panelden geri alınamıyor; yanlış bir ret ancak yeni bir kayıtla düzeltilir.`,
      onayEtiketi: 'Okudum, kanıtı reddediyorum',
      onayUyarisi: 'Reddetmeden önce onay kutusunu işaretleyin.',
      reddedildi: (tutar: string) => `Kanıt reddedildi: ${tutar}. Hiçbir tutar tahsilata yazılmadı.`,
      vazgec: 'Retten vazgeç',
    },
    csvIndir: 'Finans CSV dosyasını indir',
    csvUretildi: 'Finans CSV dosyası geçerli yetkili veritabanı görünümünden üretildi',

    /**
     * Sipariş özetindeki dokuz rakamın etiketi.
     *
     * Rakamların kendisi `data-testid`li ayrı elemanlarda duruyor (mevcut testler o elemanın
     * metnini ham değerle karşılaştırıyor); etiket **yanına** yazılıyor, değerin içine değil.
     */
    ozet: {
      paraBirimi: 'Para birimi',
      ciro: 'Ciro',
      maliyet: 'Maliyet',
      brutKar: 'Brüt kâr',
      marj: 'Kâr marjı',
      acikBakiye: 'Açık bakiye',
      dogrulanmisTahsilat: 'Doğrulanmış tahsilat',
      dogrulanmamisKanit: 'Doğrulanmamış kanıt',
      iadeEdilen: 'İade edilen',
      mutabakatDurumu: 'Mutabakat durumu',
      maliyetSayisi: 'Maliyet kaydı sayısı',
    },

    kanitBasligi: 'Ödeme kanıtı',
    paraAlindi: 'para alındı',
    paraAlinmadi: 'yalnız kanıt: para alınmadı',
    dogrula: 'Doğrula',
    zatenDogrulandi: 'Zaten doğrulanmış; yineleme fazladan hiçbir tutar yazmadı',
    kanitDogrulandi: (tur: string, tutar: string) => `${tur} kanıtı doğrulandı: ${tutar}`,

    kanitKaydetBasligi: 'Kanıt kaydet',
    kanitTuruEtiketi: 'Ödeme kanıtının türü',
    /**
     * Ödeme formunun iki alanı da **boş** doğuyor.
     *
     * Ölçülen kusur: form `40000` tutarı ve "Kısmi" türüyle açılıyordu (`useState('40000')`,
     * `useState('PARTIAL')`). 990,00 avroluk bir siparişte tek bir dalgın tık, 400,00 avroluk
     * bir kısmi ödeme yazıyordu. Bir tutarı önceden doldurmak zaman kazandırıyor gibi görünür;
     * yanlış doldurulmuş bir ödeme kaydını geri almak bir alanı elle yazmaktan çok daha pahalı.
     *
     * Boş kalması yetmiyor, **hangi tutarın beklendiği** de yazılı: açık bakiye alanın altında
     * ipucu olarak duruyor, ama alana kendiliğinden yazılmıyor. Söylemek ile yazmak aynı şey
     * değil: biri okunur, öteki imzalanır.
     */
    kanitTuruSecin: 'Ödeme türünü seçin',
    kanitTuruSecinUyari: 'Ödemenin türünü seçin: kısmi mi, son ödeme mi.',
    kanitTutariEtiketi: 'Ödeme tutarı',
    kanitTutariIpucu: (acik: string) => `Bu siparişin açık bakiyesi ${acik}. Tahsil edilen tutarı yazın.`,
    kanitKaydet: 'Ödeme kanıtı kaydet',
    kanitYinelendi: 'Yinelenen kanıt yok sayıldı; özgün kayıt döndü',
    kanitKaydedildi: 'Kanıt kaydedildi. Kaydetmek almak değildir: hâlâ doğrulanması gerekiyor.',

    maliyetBasligi: 'Maliyetler',
    kalemSecin: 'Sipariş kalemi seçin',
    kalemSatiri: (sira: string, aciklama: string) => `${sira} · ${aciklama}`,
    maliyetTedarikcisiEtiketi: 'Maliyetin tedarikçisi',
    maliyetTutariEtiketi: 'Maliyet tutarı',
    maliyetAciklamasi: (tedarikci: string) => `${tedarikci} tedarikçi maliyeti`,
    maliyetKaydet: 'Maliyet kaydet',
    maliyetKaydedildi: 'Maliyet kaydı oluşturuldu',
    maliyetSatiri: (tedarikci: string, tutar: string, kapsam: string) => `${tedarikci} · ${tutar} · ${kapsam}`,
    kalemKapsami: (kalemId: string) => `kalem ${kalemId}`,
    siparisKapsami: 'sipariş düzeyi',

    iadeBasligi: 'İade ve iptal muhasebesi',
    iadeTutariEtiketi: 'İade tutarı',
    iadeGerekcesiEtiketi: 'İade gerekçesi',
    iadeTalepEt: 'İade talep et',
    iadeVizeYok: 'Vizede iade yok: bu sipariş yalnız vize kalemi içeriyor, iade başlatılamaz.',
    iadeVizePayi: (vizeTutari: string, ust: string) => `Vizede iade yok: vize kalemi (${vizeTutari}) iade dışı. Bu siparişte en çok ${ust} iade talep edilebilir.`,
    iadeTalepEdildi: 'İade talep edildi; hiçbir ödeme aktarımı yapılmıyor (NOT_CONFIGURED)',
    iadeSatiri: (durum: string, tutar: string, gerekce: string) => `${durum} · ${tutar} · ${gerekce}`,
    onayla: 'Onayla',
    reddet: 'Reddet',
    onayNotu: 'Onaylanan sentetik muhasebe düzeltmesi',
    retNotu: 'Reddedilen sentetik muhasebe düzeltmesi',
    iadeKarariKaydedildi: 'İade kararı kaydedildi; ödeme aktarımı NOT_CONFIGURED olarak kalıyor',
    iadeReddedildi: 'İade reddedildi',

    mutabakatBasligi: 'Mutabakat',
    mutabakatHazirla: 'Mutabakatı hazır olarak işaretle',
    mutabakatHazir: 'Mutabakat hazır',
    mutabikIsaretle: 'Mutabık olarak işaretle',
    mutabikOldu: 'Sipariş mutabık',

    iadeKararlariBasligi: 'İade kararları',
    iadeyiOnayla: 'İadeyi onayla',
    iadeyiReddet: 'İadeyi reddet',
    mudurOnayNotu: 'Müdürün onayladığı sentetik muhasebe düzeltmesi',
    mudurRetNotu: 'Müdürün reddettiği sentetik muhasebe düzeltmesi',
    iadeOnaylandi: 'İade bir muhasebe kaydı olarak onaylandı; ödeme aktarımı NOT_CONFIGURED',
  },

  gozetim: {
    baslik: 'Birimler arası gözetim',
    ilgiGerekiyor: 'İLGİ GEREKİYOR',
    istisnaBasligi: 'İstisna kuyrukları',
    istisnaYukleniyor: 'İstisna kuyrukları yükleniyor…',
    istisnaKuyruklari: {
      overdue: 'Süresi geçmiş görevler',
      blocked: 'Bloke veya başarısız',
      paymentGap: 'Ödeme açığı',
      reconciliationVariance: 'Mutabakat farkı',
      lowMargin: 'Düşük kâr marjı',
    },
    kayitlariIncele: 'Aşağıdaki etkilenen kayıtları inceleyin',
    istisnaYok: 'Şu an istisna yok',
    kayit: 'kayıt',
    /** Zaman çizelgesi satırı: varlık, önceki durum, sonraki durum, eylemi yapan. */
    cizelgeSatiri: (varlik: string, onceki: string, sonraki: string, aktor: string) =>
      `${varlik}: ${onceki} → ${sonraki} · ${aktor}`,
    darBogazSatiri: (kod: string, durum: string, gerekce: string, imaEdiyor: string) =>
      `${kod} · ${durum} · ${gerekce} · başarı ima ediyor: ${imaEdiyor}`,
    /**
     * Denetim satırı: sonuç, rolü yapan **ve ne zaman**.
     *
     * `actorRole` uçtan geliyor, spesifik kullanıcı adı gelmiyor (`Oversight.auditTrail`
     * `timeline`in aksine `actor.displayName` taşımıyor) — bu bir ekran eksiği değil, uç
     * sözleşmesinin eksiği; API tarafına bildirildi. Burada gösterilebilen en doğru "kim",
     * rol adı.
     */
    denetimSatiri: (sonuc: string, rol: string, zaman: string) => `· ${sonuc} · ${rol} · ${zaman}`,
    marjGizli: 'Finansal rakamlar finance:read yetkisi istiyor',
  },

  /**
   * Denetim maddesi B11: bağımsız "Tüm denetim kaydı" ekranı.
   *
   * Yukarıdaki `gozetim.denetimSatiri` tek bir siparişin zaman çizelgesine bağlı; bu ekran
   * `GET /audit` üstünden bütün denetim defterini sayfalayarak okuyor, hiçbir siparişle sınırlı
   * değil. İzin de farklı: `audit:read`, `oversight:read`den ayrı bir yetki.
   */
  genelDenetim: {
    baslik: 'Tüm denetim kaydı',
    aciklama: 'Bütün denetim defteri, tek bir siparişle sınırlı değil.',
    eylemEtiketi: 'Eylem',
    hedefTipiEtiketi: 'Hedef tipi',
    tumEylemler: 'Tüm eylemler',
    tumHedefTipleri: 'Tüm hedef tipleri',
    sutun: {
      an: 'Zaman',
      eylem: 'Eylem',
      aktor: 'Aktör',
      hedef: 'Hedef',
      sonuc: 'Sonuç',
    },
    bos: 'Kayıt yok',
    hata: 'Denetim kaydı okunamadı',
    sayac: (gosterilen: string, toplam: string) => `${toplam} kaydın ${gosterilen} tanesi`,
    sayfa: (sayfa: string) => `Sayfa ${sayfa}`,
  },

  /**
   * Denetim olay kodlarının okunur karşılığı.
   *
   * `entry.action` (`lead.created`, `quote_version.published`) hâlâ `<code>` içinde, olduğu
   * gibi: denetim izninin değeri veritabanına yazılanla **birebir aynı** olmasında (yorum
   * `Workflow.tsx` `OversightPanel` içinde). Burası onun yanına okunur bir karşılık ekliyor,
   * onu değiştirmiyor. Karşılığı olmayan kod boş kalıyor; uydurma bir cümle yazılmıyor.
   */
  eylem: {
    'auth.login.failed': 'Giriş denemesi başarısız',
    'auth.login.rate_limited': 'Giriş denemesi sınırlandı',
    'auth.login.succeeded': 'Giriş yapıldı',
    'auth.logout': 'Çıkış yapıldı',
    'auth.sessions.revoked_all': 'Bütün oturumlar kapatıldı',
    'configuration.created': 'Yapılandırma oluşturuldu',
    'configuration.updated': 'Yapılandırma güncellendi',
    'cost_entry.recorded': 'Maliyet kaydedildi',
    'customer.created': 'Müşteri oluşturuldu',
    'customer.merge_rejected': 'Müşteri birleştirme reddedildi',
    'customer.merged': 'Müşteri birleştirildi',
    'customer.note_added': 'Müşteriye not eklendi',
    'customer.owner_assigned': 'Müşteri sahibi atandı',
    'customer.updated': 'Müşteri güncellendi',
    'details.updated': 'Ayrıntılar güncellendi',
    'document.downloaded': 'Belge indirildi',
    'document.integrity_failed': 'Belge bütünlük denetimi başarısız',
    'document.uploaded': 'Belge yüklendi',
    'export.orders.csv': 'Sipariş CSV dışa aktarıldı',
    'fulfilment_task.assigned': 'Görev atandı',
    'fulfilment_task.created': 'Görev oluşturuldu',
    'fulfilment_task.details_updated': 'Görev ayrıntıları güncellendi',
    'fulfilment_task.note_added': 'Göreve not eklendi',
    'fulfilment_task.operations_updated': 'Görev önceliği güncellendi',
    'fulfilment_task.status_changed': 'Görev durumu değişti',
    'integration.webhook.recorded': 'Dış bildirim kaydedildi',
    'integration.webhook.refused': 'Dış bildirim reddedildi',
    'lead.converted': 'Aday fırsata dönüştürüldü',
    'lead.created': 'Aday oluşturuldu',
    'opportunity.created': 'Fırsat oluşturuldu',
    'opportunity.details_updated': 'Fırsat ayrıntıları güncellendi',
    'opportunity.stage_changed': 'Fırsat aşaması değişti',
    'order.created': 'Sipariş oluşturuldu',
    'order.status_changed': 'Sipariş durumu değişti',
    'payment_evidence.recorded': 'Ödeme kanıtı kaydedildi',
    'payment_evidence.rejected': 'Ödeme kanıtı reddedildi',
    'payment_evidence.verified': 'Ödeme kanıtı doğrulandı',
    'quote.accepted': 'Teklif kabul edildi',
    'quote.created': 'Teklif oluşturuldu',
    'quote.rejected': 'Teklif reddedildi',
    'quote_version.created': 'Teklif sürümü oluşturuldu',
    'quote_version.published': 'Teklif sürümü yayımlandı',
    'quote_version.superseded': 'Teklif sürümünün yerine yenisi geçti',
    'reconciliation.recomputed': 'Mutabakat yeniden hesaplandı',
    'reconciliation.status_changed': 'Mutabakat durumu değişti',
    'refund.decided': 'İade kararı verildi',
    'refund.requested': 'İade talep edildi',
    'supplier.created': 'Tedarikçi oluşturuldu',
    'supplier.question_opened': 'Tedarikçiye soru açıldı',
    'supplier.updated': 'Tedarikçi güncellendi',
    'supplier_booking.created': 'Tedarikçi rezervasyonu oluşturuldu',
    'supplier_booking.status_changed': 'Tedarikçi rezervasyonu durumu değişti',
    'user.created': 'Kullanıcı oluşturuldu',
    'user.password_set': 'Kullanıcı parolası ayarlandı',
    'user.role_changed': 'Kullanıcı rolü değişti',
    'user.updated': 'Kullanıcı güncellendi',
    'voucher.issued': "Voucher düzenlendi",
  } as Readonly<Record<string, string>>,

  entegrasyon: {
    baslik: 'Entegrasyon hazırlığı',
    denetleniyor: 'Yapılandırma denetleniyor…',
    okunamadi: 'Yapılandırma okunamadı; dış bir başarı ima edilmiyor.',
    canliCagriSerbest: 'Geçerli yapılandırma canlı çağrılara izin veriyor.',
    eksikYapilandirma: (adet: string) => `Eksik yapılandırma (${adet})`,
    /**
     * Sağlayıcı anahtarının ekran adı.
     *
     * `entry.key` (`chatwoot`, `paximum`, `globdmc`…) bir iç sistem adı, operatörün tanıması
     * gereken bir kelime değil. `api/src/common/config/env.ts`teki `authority` alanı zaten bu işi
     * yapan bir kanaldı ve **hep vardı**; bant burada `entry.key`i çiziyordu, `authority`yi değil.
     * Bir kez düzeltilip ekrandan kaldırılan iç servis adlarının Genel Bakış'a geri gelmesinin
     * sebebi buydu — düzeltme tek ekranda kaldı, `entry.key`yi çizen tek yer değil bu bileşendi ve
     * regresyon testi (`onyuz-kalite.spec.ts`) bu yedi kelimeyi hiç tanımıyordu. Şimdi hem bileşen
     * `authority`yi çiziyor hem test bu yedi kelimeyi biliyor.
     */
    saglayiciAdi: {
      chatwoot: 'Müşteri iletişimi',
      'commerce-psp': 'Sipariş tahsilatı',
      'nomod-checkout': 'Teklif ödemesi',
      paypartner: 'Fatura',
      paximum: 'Otel tedariki',
      globdmc: 'Tur ve paket tedariki',
      /**
       * Ölçüldü 28.08.2026: sekizinci sağlayıcı (`ai-content`) uca eklendi, bu tabloya
       * eklenmedi ve Genel Bakış ekranında kartın başlığı **`ai-content`** yazıyordu — tam
       * olarak bu tablonun var olma sebebi olan kusur, sekizincisinde geri geldi.
       *
       * Regresyon testi (`onyuz-kalite.spec.ts`) onu yakalayamadı çünkü yedi anahtarı elle
       * sayıyordu; test artık listeyi uçtan okuyor, yani dokuzuncu sağlayıcı da bu satırı
       * yazmadan geçemeyecek.
       */
      'ai-content': 'Teklif asistanı',
    } as Readonly<Record<string, string>>,
  },

  saltOkunur: {
    baslik: 'Akış görünürlüğü',
    aciklama: 'Salt okunur görünüm. Finansal ayrıntılar ve bütün akış eylemleri kapalı.',
    boruHatti: 'Boru hattı',
    siparisler: 'Siparişler',
    firsatSatiri: (kod: string, baslik: string, asama: string) => `${kod} · ${baslik} · ${asama}`,
    siparisSatiri: (kod: string, musteri: string, durum: string, kalem: string) =>
      `${kod} · ${musteri} · ${durum} · ${kalem}`,
  },
} as const;

/**
 * WhatsApp gönderim durumlarının okunur karşılığı (`whatsapp-send.service.ts`).
 *
 * Kod değeri **ekrandan kaldırılmıyor**, yanına yazılıyor: operatör Türkçesini okuyor, destek
 * isteğinde ise sunucunun kullandığı adı verebiliyor. `ortak.ts`teki `DURUM_ADI` deseninin
 * aynısı; oraya konmadı çünkü bu beş değer yalnız bu ekranda geçiyor ve `DURUM_ADI` zaten
 * altı ayrı tablonun durumlarını taşıyor.
 */
export const WHATSAPP_DURUM_ADI: Readonly<Record<string, string>> = {
  SENT: 'Gönderildi',
  NOT_CONFIGURED: 'WhatsApp sağlayıcısı tanımlı değil',
  BLOCKED_CONSENT: 'Müşterinin rızası yok ya da geri çekilmiş',
  BLOCKED_WINDOW: '24 saatlik pencere kapalı ve bu amaç için onaylı şablon yok',
  BLOCKED_MEDIA: 'Ek dosya WhatsApp sınırını aşıyor',
  FAILED: 'Sağlayıcı isteği reddetti',
};

/** Gönderim amaçları (`whatsapp-template.dto.ts`), satışçının gördüğü üçü. */
export const WHATSAPP_AMAC_ADI: Readonly<Record<string, string>> = {
  HOTEL_ROOM_PHOTOS: 'Otel ve oda fotoğrafı',
  ACTIVITIES_PDF: 'Aktiviteler listesi',
  QUOTE_PAYMENT_LINK: 'Teklif ve ödeme bağlantısı',
};
